import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import './styles.css';
import './process-fix.css';
import './hero-match.css';
import './nav-services.css';
import './pricing-footer.css';
import './button-final-fix.css';

const phone = '+1-9595000099';
const phoneHref = '+19595000099';
const email = 'btpermits@gmail.com';
const navigationWhatsappMessage = "Hi, I am want to know more about your Permit services. Please let me know when you're available for a quick discussion.";
const navigationWhatsappUrl = `https://wa.me/19595000099?text=${encodeURIComponent(navigationWhatsappMessage)}`;
const brandLogo = '/assets/btp-logo-dark.png';
const footerLogo = '/assets/btp-logo-footer-light.png';

const imageDimensions = {
  'btp-logo-dark.png': [1032, 253],
  'btp-logo-footer-light.png': [1032, 253],
  'hero-consultant.jpg': [1536, 1024],
  'slider-registration.jpg': [1536, 1024],
  'slider-permits.jpg': [1536, 1024],
  'permit-compliance-desk.jpg': [1536, 1024],
  'hero-truck-permits.jpg': [1693, 929],
  'blog-irp-apportioned-registration-plates.jpg': [1536, 1024],
  'blog-new-trucking-company-registration-dot-mc.jpg': [1727, 911],
  'blog-ifta-fuel-tax-filing-mileage-records.jpg': [1536, 1024],
  'blog-boc3-mc-authority-process-agent.jpg': [1727, 911],
  'blog-state-truck-permits-kyu-nyhut-nmwdt.jpg': [1536, 1024],
  'blog-dispatch-paperwork-factoring-carrier-packet.jpg': [1672, 941]
};

function SiteImage({ src, width, height, sizes, ...props }) {
  const filename = typeof src === 'string' ? src.split('/').pop()?.split('?')[0] : '';
  const dimensions = imageDimensions[filename] || [1200, 800];
  const responsive = typeof src === 'string' && src.startsWith('/assets/') && src.endsWith('.jpg') && imageDimensions[filename];
  const base = responsive ? src.slice(0, -4) : '';
  return React.createElement('img', {
    src,
    width: width || dimensions[0],
    height: height || dimensions[1],
    decoding: 'async',
    srcSet: responsive ? `${base}-480.jpg 480w, ${base}-960.jpg 960w, ${src} ${dimensions[0]}w` : undefined,
    sizes: responsive ? (sizes || '(max-width: 768px) 100vw, 50vw') : undefined,
    ...props
  });
}

function ArrowIcon() {
  return <span className="btn-arrow-icon" aria-hidden="true"></span>;
}

function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 6h16v12H4z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="m4 7 8 6 8-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M7.4 4.5 9.7 7c.5.5.5 1.3.1 1.9l-1 1.4c1.2 2.4 3.1 4.3 5.6 5.6l1.4-1c.6-.4 1.4-.3 1.9.2l2.4 2.3c.6.6.6 1.5.1 2.1-.8 1-2 1.5-3.3 1.3-6.8-.9-12-6.1-12.8-12.8-.2-1.3.3-2.5 1.3-3.3.6-.6 1.5-.6 2 0Z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M5 6.5h14v9H9l-4 3v-12Z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M9 11h.1M12 11h.1M15 11h.1" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

const navItems = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Services', path: '/services' },
  { label: 'Packages', path: '/packages' },
  { label: 'Factoring', path: '/factoring' },
  { label: 'Blog', path: '/blog' },
  { label: 'FAQ', path: '/faq' },
  { label: 'Contact', path: '/contact' }
];

const services = [
  {
    title: 'New Trucking Company Registration',
    slug: 'company-registration',
    label: 'Company registration',
    summary: 'Start your authority with LLC guidance, EIN checklist, DOT and MC setup, and compliance paperwork support.',
    hero: 'Build your trucking company on a clean registration foundation with the right filings from day one.',
    overview: 'We help new carriers put business registration, EIN details, USDOT and operating authority filings, BOC-3, UCR, insurance coordination, and vehicle permits in a workable order. You receive a clear document checklist and next-step guidance for the filings that apply to your operation.',
    details: ['USDOT and MC number filing', 'New authority startup checklist', 'FMCSA compliance guidance', 'Carrier packet readiness']
  },
  {
    title: 'MC & USDOT Registration',
    slug: 'dot-mc-registration',
    label: 'Federal registration',
    summary: 'Get active with the federal numbers carriers need to haul freight legally across state lines.',
    hero: 'Federal authority filing support for carriers preparing to operate legally and confidently.',
    overview: 'A USDOT number identifies a carrier for federal safety oversight, while operating authority is required for certain for-hire interstate operations. We review your operation type, prepare the information needed for registration, and help you understand the insurance, BOC-3, and status steps that may follow.',
    details: ['DOT application review', 'MC authority filing', 'Status tracking', 'Common rejection prevention']
  },
  {
    title: 'BOC-3 Filing',
    slug: 'boc-3-filing',
    label: 'Process agent filing',
    summary: 'File process agent coverage so your motor carrier authority can move toward activation.',
    hero: 'Fast process-agent filing support so your motor carrier authority can keep moving.',
    overview: 'Form BOC-3 designates process agents for motor carriers, brokers, and freight forwarders with operating authority. We coordinate the filing, provide confirmation, and explain how it fits with the insurance and authority activation process.',
    details: ['Nationwide process agent filing', 'Fast confirmation', 'Authority activation support', 'New entrant guidance']
  },
  {
    title: 'IFTA, IRP & UCR Permits',
    slug: 'ifta-irp-ucr',
    label: 'Interstate credentials',
    summary: 'Keep interstate trucks compliant with fuel tax, apportioned plates, UCR, and renewal support.',
    hero: 'Interstate permit support for fuel tax, apportioned plates, and annual filing requirements.',
    overview: 'Interstate carriers may need separate credentials for fuel-tax reporting, apportioned vehicle registration, and annual UCR registration. We identify which programs apply, organize vehicle and mileage records, and help with setup, renewals, and recurring filings.',
    details: ['IFTA account setup and returns', 'IRP plates and cab cards', 'UCR annual filing', 'Renewal reminders']
  },
  {
    title: 'State Truck Permits',
    slug: 'state-truck-permits',
    label: 'State permit support',
    summary: 'Handle operating permits such as KYU, New York HUT, New Mexico WDT, Oregon permits, and more.',
    hero: 'State-specific permit guidance for carriers crossing jurisdictions with different rules.',
    overview: 'State permit needs depend on the route, vehicle weight, cargo, account status, and whether the trip is temporary or recurring. We help carriers plan for programs such as KYU, New York HUT, New Mexico weight-distance tax, Oregon credentials, and trip permits before dispatch.',
    details: ['State-by-state permit checklist', 'Temporary trip permit help', 'Oversize and overweight guidance', 'Renewal support']
  },
  {
    title: 'Truck Dispatch Service',
    slug: 'truck-dispatch',
    label: 'Dispatch operations',
    summary: 'Get load booking, rate negotiation, paperwork, broker communication, and factoring support.',
    hero: 'Dispatch support that helps carriers stay loaded, organized, and paperwork-ready.',
    overview: 'Our dispatch support helps owner-operators and small fleets search for suitable loads, communicate with brokers, negotiate rates, and keep carrier packets, confirmations, delivery documents, and invoices organized.',
    details: ['Load planning and booking', 'Rate negotiation', 'Carrier packets and paperwork', 'Dedicated dispatcher support']
  }
];

function articleHtml({ intro, sections, checklist, questions, finalNote }) {
  const sectionHtml = sections
    .map((section) => `<h2>${section.heading}</h2>${section.paragraphs.map((paragraph) => `<p>${paragraph}</p>`).join('')}`)
    .join('');
  const checklistHtml = checklist?.length
    ? `<h2>Documents and details to prepare</h2><ul>${checklist.map((item) => `<li>${item}</li>`).join('')}</ul>`
    : '';
  const questionHtml = questions?.length
    ? `<h2>Quick answers carriers ask</h2>${questions.map((item) => `<h3>${item.question}</h3><p>${item.answer}</p>`).join('')}`
    : '';
  const serviceContext = `<h2>Before you file</h2><p>Confirm the legal business name, physical address, contact information, vehicle details, operating states, insurance status, and any agency notices before submitting an application. Consistent information across authority, insurance, tax, registration, and permit records can prevent avoidable correction cycles.</p><p>Requirements and processing times vary by agency and operation. Best Truck Permits provides administrative filing support and document guidance; government agencies and third parties make final approval decisions.</p><h2>Keep one complete compliance file</h2><p>Store authority records, insurance certificates, permit credentials, cab cards, tax filings, state account numbers, vehicle documents, and renewal confirmations together. Review the file before adding a truck, changing an address, entering a new state, or accepting a load on an unfamiliar route.</p><h2>Official resources</h2><ul><li><a href="https://www.fmcsa.dot.gov/registration" target="_blank" rel="noreferrer">FMCSA registration guidance</a></li><li><a href="https://plan.ucr.gov/do-i-need-to-register/" target="_blank" rel="noreferrer">UCR registration eligibility guide</a></li><li><a href="https://www.iftach.org/" target="_blank" rel="noreferrer">International Fuel Tax Association</a></li><li><a href="https://www.irponline.org/" target="_blank" rel="noreferrer">International Registration Plan</a></li></ul>`;
  return `<p>${intro}</p>${sectionHtml}${checklistHtml}${questionHtml}${serviceContext}<p>${finalNote}</p>`;
}

const seedPosts = [
  {
    id: 'irp-plates-guide',
    title: 'How to Get IRP Plates and Apportioned Registration',
    category: 'IRP Plates',
    date: '2026-05-10',
    imageUrl: '/assets/blog-irp-apportioned-registration-plates.jpg',
    excerpt: 'A practical guide to IRP plates, who needs apportioned registration, and what documents carriers should prepare.',
    content: articleHtml({
      intro: 'IRP plates, also called apportioned plates, help interstate carriers register commercial vehicles that travel in more than one jurisdiction. For a new trucking company, IRP can feel confusing because it touches vehicle ownership, business address, insurance, heavy vehicle use tax, estimated mileage, and state motor carrier account rules. This guide explains the practical order of the process so an owner-operator or fleet can prepare clean documents before requesting apportioned registration.',
      sections: [
        {
          heading: 'What IRP plates mean for interstate trucking',
          paragraphs: [
            'The International Registration Plan lets qualifying commercial vehicles pay registration fees based on the miles operated in each participating state or province. Instead of buying separate full registration in every state, a carrier receives apportioned credentials from its base jurisdiction. The vehicle can then operate across IRP jurisdictions when the cab card and plate are active and the carrier follows the rules for weight, insurance, mileage records, and renewals.',
            'Many carriers search for IRP plates after they receive a truck, book interstate freight, or start preparing for broker setup. If the vehicle will cross state lines and meets the weight or operational requirements, IRP may be required before legal operation. A carrier should not wait until the first load is ready to move because missing ownership documents, insurance information, 2290 proof, or address records can delay the application.'
          ]
        },
        {
          heading: 'Who usually needs apportioned registration',
          paragraphs: [
            'A commercial motor vehicle commonly needs IRP when it is used in two or more jurisdictions and has a gross vehicle weight, registered weight, or combined weight over 26,000 pounds, or when it has three or more axles regardless of weight. Some operations have exceptions, so the correct answer depends on the vehicle, route, business model, and state rules. Best Truck Permits reviews the practical filing path and helps carriers understand what information should be gathered before the account is started.',
            'Owner-operators with new authority often need IRP along with USDOT, MC authority, BOC-3, UCR, IFTA, insurance, and state-specific permits. Established fleets may need IRP for adding trucks, replacing plates, renewing cab cards, changing weights, or correcting account details. The earlier the carrier organizes mileage estimates and vehicle documents, the easier the filing becomes.'
          ]
        },
        {
          heading: 'How the IRP application process usually works',
          paragraphs: [
            'The process normally begins by confirming the base jurisdiction, business identity, physical address, vehicle information, insurance, and proof of heavy vehicle use tax when applicable. The state may ask for lease agreements, titles, purchase documents, proof of established place of business, driver or owner identification, DOT information, and prior registration history. If the carrier is new and has no actual mileage history, the jurisdiction may use estimated distance rules.',
            'After the application is prepared, the state reviews the information, calculates fees, and issues credentials after payment. Errors in the business name, VIN, weight, address, or insurance can create rejection or correction cycles. That is why clean document preparation matters. A permit support team cannot control agency approval speed, but it can help reduce avoidable back-and-forth caused by incomplete paperwork.'
          ]
        },
        {
          heading: 'Renewal planning and mileage records',
          paragraphs: [
            'IRP is not a one-time task. Carriers must renew credentials and keep mileage records that support the distances reported. Trip sheets, ELD records, odometer readings, routes, fuel receipts, dispatch notes, and settlement documents can all become important. Good recordkeeping protects the carrier if a state reviews the account or asks for support during renewal.',
            'Many fleets connect IRP planning with IFTA fuel tax filing because both depend on jurisdictional mileage. A consistent mileage process helps with quarterly IFTA returns, IRP renewals, state mileage taxes, and internal cost tracking. If you also need dispatch help after registration, Best Truck Permits can point carriers toward dispatch resources such as <a href="https://www.besttruckdispatch.com" target="_blank" rel="noreferrer">Best Truck Dispatch</a> for load support conversations.'
          ]
        }
      ],
      checklist: ['Legal business name and EIN information', 'USDOT and MC details if applicable', 'Vehicle title, bill of sale, lease, or ownership proof', 'VIN, unit number, year, make, model, and declared weight', 'Insurance information matching the business and vehicle', 'Proof of address or established place of business documents', 'Form 2290 Schedule 1 when required', 'Prior mileage records or estimated mileage details for new carriers'],
      questions: [
        { question: 'Can I get IRP plates before my MC authority is active?', answer: 'Some jurisdictions may allow parts of the registration process to move forward while authority is pending, but operating requirements still depend on insurance, authority status, and state rules. The best step is to review the full setup path before assuming plates alone make the truck ready to haul.' },
        { question: 'Do IRP plates replace IFTA?', answer: 'No. IRP is for apportioned vehicle registration, while IFTA is for fuel tax reporting. Many interstate carriers need both, and both depend on accurate mileage records.' },
        { question: 'How long does IRP take?', answer: 'Timing depends on the base state, document readiness, payment, account status, and whether the application needs corrections. Clean documents usually make the process smoother.' }
      ],
      keywords: ['IRP plates', 'apportioned registration', 'IRP renewal', 'cab card', 'interstate trucking permits', 'Form 2290', 'truck registration service'],
      finalNote: 'Best Truck Permits helps carriers prepare IRP plate and apportioned registration requests with a practical checklist, document review, and filing support so the registration step fits into the larger trucking company setup plan.'
    })
  },
  {
    id: 'register-trucking-company',
    title: 'How to Register a New Trucking Company in the USA',
    category: 'Company Setup',
    date: '2026-04-28',
    imageUrl: '/assets/blog-new-trucking-company-registration-dot-mc.jpg',
    excerpt: 'The startup path for owner-operators: business structure, EIN, USDOT, MC authority, BOC-3, UCR, IFTA, and IRP.',
    content: articleHtml({
      intro: 'Registering a new trucking company in the United States is more than filing one form. A carrier may need a legal business entity, EIN, USDOT number, MC authority, BOC-3, UCR, insurance, IFTA, IRP plates, state permits, factoring setup, dispatch paperwork, and a clean carrier packet before brokers and shippers can review the company. This guide explains the sequence in plain language so new owner-operators can avoid common startup delays.',
      sections: [
        {
          heading: 'Start with the business foundation',
          paragraphs: [
            'Most carriers begin by choosing a business structure, registering the company name, and obtaining an EIN from the IRS. The exact entity choice should be reviewed with a qualified tax or legal adviser, but the practical trucking setup needs consistent business information across every filing. The name, address, responsible party, phone number, and email should be accurate because the same details may appear on federal, state, bank, insurance, factoring, and broker documents.',
            'A messy foundation creates problems later. If the business address changes mid-process, if the name is spelled differently, or if the owner information does not match, the carrier may face delays with DOT, insurance, IFTA, IRP, or factoring applications. Best Truck Permits helps customers organize the filing path and identify which details should be ready before the next step.'
          ]
        },
        {
          heading: 'USDOT and MC authority',
          paragraphs: [
            'A USDOT number identifies the motor carrier for safety monitoring and compliance records. MC authority, also called operating authority, is required for many for-hire interstate carriers that transport regulated commodities. Some carriers need both, while others may need only a USDOT number depending on operation type, cargo, vehicle, and route.',
            'After the federal filing is submitted, the carrier still needs insurance filings, BOC-3, and authority status monitoring. New authority does not become active simply because an application was submitted. The carrier should watch for FMCSA status changes and avoid booking freight before all required pieces are active and properly documented.'
          ]
        },
        {
          heading: 'BOC-3, UCR, IFTA, and IRP',
          paragraphs: [
            'BOC-3 designates process agents for motor carrier authority. UCR is a registration requirement for many interstate carriers. IFTA supports fuel tax reporting for qualifying interstate vehicles, and IRP handles apportioned registration plates. These pieces are often discussed together because new carriers need them around the same time, but they are separate requirements with separate rules.',
            'A practical startup checklist should map the filing order. For example, a carrier may need DOT and MC details before other accounts can be completed, proof of insurance before authority activates, and vehicle documents before IRP plates are issued. When carriers search for trucking company registration help, they usually need guidance across the whole path rather than one isolated form.'
          ]
        },
        {
          heading: 'Carrier packet, factoring, and dispatch readiness',
          paragraphs: [
            'Once authority and permits are moving, a new carrier should prepare the paperwork brokers and factoring companies request. This can include W-9, certificate of insurance, operating authority, voided check, carrier profile, safety contacts, factoring notice, and equipment details. Dispatch paperwork should also be organized so load booking does not stall after the first broker conversation.',
            'Factoring setup can help new trucking companies turn invoices into faster cash flow after delivered loads. Dispatch support can help carriers search loads, communicate with brokers, negotiate rates, and handle paperwork. These services do not replace compliance obligations, but they help the company move from registration to daily operations.'
          ]
        }
      ],
      checklist: ['Business registration information and EIN', 'Owner or responsible party details', 'Business address and contact information', 'USDOT and MC application details', 'Insurance agent information', 'BOC-3 process agent filing', 'UCR, IFTA, IRP, and state permit needs', 'Carrier packet documents for broker setup'],
      questions: [
        { question: 'Can I start a trucking company without a truck?', answer: 'Some business and authority filings can begin before a truck is purchased, but IRP, IFTA, insurance, and broker readiness often require vehicle and insurance information. The correct sequence depends on the carrier plan.' },
        { question: 'What is the biggest mistake new carriers make?', answer: 'A common mistake is filing pieces out of order or using inconsistent information. Another mistake is assuming authority is active before insurance, BOC-3, and status checks are complete.' },
        { question: 'Do I need factoring before I book loads?', answer: 'Not always, but many new carriers arrange factoring early so payment terms do not create cash-flow pressure after the first invoices are submitted.' }
      ],
      keywords: ['register trucking company', 'new trucking company registration', 'USDOT registration', 'MC authority filing', 'BOC-3 filing', 'UCR registration', 'carrier packet setup'],
      finalNote: 'Best Truck Permits supports new trucking company registration by helping carriers understand the filing path, organize documents, and prepare the permit, authority, and compliance items needed for a stronger launch.'
    })
  },
  {
    id: 'ifta-fuel-tax',
    title: 'IFTA Fuel Tax Filing: What Truckers Should Know',
    category: 'IFTA',
    date: '2026-04-12',
    imageUrl: '/assets/blog-ifta-fuel-tax-filing-mileage-records.jpg',
    excerpt: 'IFTA keeps fuel tax reporting organized for interstate carriers. Here is what to track before filing.',
    content: articleHtml({
      intro: 'IFTA fuel tax filing is one of the most important recurring compliance tasks for interstate trucking companies. The International Fuel Tax Agreement helps qualifying carriers report fuel use across jurisdictions through a base state account. The filing itself may look simple, but the accuracy depends on good mileage, fuel, route, and receipt records throughout the quarter.',
      sections: [
        {
          heading: 'What IFTA is designed to do',
          paragraphs: [
            'When a qualifying truck travels across state lines, it burns fuel in multiple jurisdictions and may buy fuel in only some of them. IFTA creates a reporting system that compares miles traveled and fuel purchased by jurisdiction, then calculates whether tax is due or credit may apply. This keeps carriers from needing separate fuel tax reports for every state they enter.',
            'A carrier usually needs IFTA if it operates a qualified motor vehicle in more than one member jurisdiction. A qualified vehicle generally has two axles and a gross vehicle weight or registered weight over 26,000 pounds, has three or more axles, or is used in combination over 26,000 pounds. Requirements can vary by operation, so carriers should review their setup before assuming they are exempt.'
          ]
        },
        {
          heading: 'What records should be tracked',
          paragraphs: [
            'IFTA depends on jurisdictional mileage and fuel purchase records. Carriers should track trip dates, origin, destination, routes, odometer readings, total miles, taxable miles, non-taxable miles when applicable, gallons purchased, fuel type, vendor, location, and receipt details. ELD data can help, but the carrier still needs records that are organized and understandable.',
            'The most painful IFTA problems often start months before the return is due. Missing receipts, unclear routes, incorrect odometer readings, and mixed personal or business fuel purchases can make the report harder to prepare. A clean weekly habit is better than trying to rebuild an entire quarter after the deadline arrives.'
          ]
        },
        {
          heading: 'IFTA deadlines and quarterly habits',
          paragraphs: [
            'IFTA returns are generally filed quarterly. Carriers should not wait until the last day to collect documents because errors can lead to penalties, interest, or amended returns. A practical system is to review mileage and fuel records every week, confirm jurisdiction totals at month-end, and prepare the return before the deadline week.',
            'IFTA also connects to IRP because both depend on distance records. If a carrier uses different mileage totals for different reports without explanation, future reviews can become more difficult. Consistency matters. The carrier should keep source records long enough to support renewals, audits, and agency questions.'
          ]
        },
        {
          heading: 'How Best Truck Permits supports IFTA',
          paragraphs: [
            'Best Truck Permits helps carriers understand IFTA setup, renewal, quarterly filing preparation, and document organization. We do not replace the carrier responsibility to keep accurate records, but we help customers identify what should be ready and how to avoid common filing delays.',
            'For new carriers, IFTA should be reviewed along with USDOT, MC authority, BOC-3, UCR, IRP plates, and state permits. For existing carriers, the focus is usually renewal, quarterly reporting, and correcting recordkeeping issues before they create bigger compliance problems.'
          ]
        }
      ],
      checklist: ['Quarterly trip sheets or ELD mileage exports', 'Fuel receipts with location and gallons', 'Odometer readings and route details', 'Jurisdiction mileage totals', 'Fuel type and unit information', 'IFTA license and decal information', 'Prior return details if available'],
      questions: [
        { question: 'Is IFTA the same as IRP?', answer: 'No. IFTA is fuel tax reporting. IRP is apportioned registration. Many interstate carriers need both, but the filings serve different purposes.' },
        { question: 'Do I file IFTA if I did not run during the quarter?', answer: 'Many jurisdictions still require a no-operation or zero return if the account is active. Carriers should not ignore a quarter just because there were no miles.' },
        { question: 'Can fuel receipts alone complete IFTA?', answer: 'No. Fuel receipts are only one part. The carrier also needs mileage by jurisdiction so the return can calculate fuel tax accurately.' }
      ],
      keywords: ['IFTA fuel tax filing', 'IFTA quarterly return', 'IFTA renewal', 'truck fuel tax', 'jurisdiction mileage records', 'IFTA permit service'],
      finalNote: 'A strong IFTA process helps carriers stay organized, avoid avoidable penalties, and keep interstate operations moving with better mileage and fuel documentation.'
    })
  },
  {
    id: 'boc-3-filing-authority-activation',
    title: 'BOC-3 Filing and MC Authority Activation: What Carriers Need to Know',
    category: 'BOC-3',
    date: '2026-03-26',
    imageUrl: '/assets/blog-boc3-mc-authority-process-agent.jpg',
    excerpt: 'Understand why BOC-3 is required, how process agents work, and how it fits into new authority activation.',
    content: articleHtml({
      intro: 'BOC-3 filing is a required step for many motor carriers, brokers, and freight forwarders before operating authority can become active. New carriers often hear about BOC-3 after filing for MC authority, but the better approach is to plan for it from the beginning. This article explains what BOC-3 does, why it matters, and how it connects to the authority activation process.',
      sections: [
        {
          heading: 'What BOC-3 means',
          paragraphs: [
            'BOC-3 stands for Designation of Process Agents. It tells FMCSA who can receive legal documents on behalf of the carrier in each required jurisdiction. In practical terms, it is a federal filing that helps complete the authority setup path for many for-hire interstate operations.',
            'A process agent is not the same as a dispatcher, factoring company, insurance agent, or permit company. The process agent role is tied to legal service of process. Because it is connected to authority activation, missing or incorrect BOC-3 filing can delay a new carrier that is trying to become load-ready.'
          ]
        },
        {
          heading: 'How BOC-3 fits with MC authority',
          paragraphs: [
            'When a carrier applies for MC authority, the authority does not become active immediately. Insurance filings, BOC-3, protest periods, and FMCSA processing all affect timing. Carriers should monitor status and avoid assuming they can haul regulated freight simply because an application was submitted.',
            'BOC-3 is often one of the easier items to complete when the carrier has the correct authority information. The challenge is not usually the form itself; it is knowing where it fits in the overall setup sequence. Best Truck Permits helps customers keep that sequence clear.'
          ]
        },
        {
          heading: 'Common BOC-3 mistakes',
          paragraphs: [
            'Common issues include using the wrong business name, misunderstanding whether authority is active, not matching the MC number correctly, or thinking BOC-3 replaces insurance. BOC-3 does not replace insurance and does not guarantee that authority will activate. It is one piece of the federal authority process.',
            'Another mistake is waiting until the carrier is ready to book a load. Brokers and shippers may verify authority status before approving a setup packet. If BOC-3 or insurance filings are missing, the carrier may lose time and possibly lose the load opportunity.'
          ]
        },
        {
          heading: 'What to do after filing',
          paragraphs: [
            'After BOC-3 is filed, the carrier should continue monitoring authority status and confirm that insurance filings are in place. The company should also prepare UCR, IFTA, IRP, state permits, W-9, certificate of insurance, and carrier packet documents as needed.',
            'A new authority checklist helps connect all these pieces. The goal is not just to file forms; the goal is to become operationally ready with documents that brokers, factoring companies, dispatchers, and agencies may request.'
          ]
        }
      ],
      checklist: ['Legal business name', 'MC number or authority information', 'USDOT number', 'Owner contact details', 'Authority type', 'Insurance agent contact', 'FMCSA status notes'],
      questions: [
        { question: 'Is BOC-3 required for every trucking company?', answer: 'Many for-hire interstate carriers need BOC-3, but requirements depend on authority type and operation. A carrier should review its FMCSA filing path before assuming it does or does not apply.' },
        { question: 'Does BOC-3 activate my authority by itself?', answer: 'No. BOC-3 is required for many authorities, but insurance filings, FMCSA processing, and other requirements also affect activation.' },
        { question: 'Can I change process agents later?', answer: 'Changes may be possible, but carriers should keep records and ensure any updates are properly filed.' }
      ],
      keywords: ['BOC-3 filing', 'process agent filing', 'MC authority activation', 'FMCSA authority', 'new trucking authority setup'],
      finalNote: 'Best Truck Permits helps carriers place BOC-3 in the correct filing sequence so new authority setup feels organized instead of scattered.'
    })
  },
  {
    id: 'state-truck-permits-guide',
    title: 'State Truck Permits: KYU, NY HUT, NM WDT, Oregon, Trip Permits and More',
    category: 'State Permits',
    date: '2026-03-08',
    imageUrl: '/assets/blog-state-truck-permits-kyu-nyhut-nmwdt.jpg',
    excerpt: 'A state-by-state permit planning guide for carriers running freight through high-requirement jurisdictions.',
    content: articleHtml({
      intro: 'State truck permits can surprise carriers because requirements change based on route, vehicle weight, cargo, mileage, and jurisdiction. A carrier may have federal authority and still need state-specific accounts or permits before entering certain states. This guide explains the most searched state permit topics and how to plan before a load is dispatched.',
      sections: [
        {
          heading: 'Why state permits matter',
          paragraphs: [
            'Federal filings such as USDOT, MC authority, BOC-3, UCR, IFTA, and IRP do not automatically satisfy every state-level requirement. Some states have mileage taxes, highway use taxes, temporary trip permits, weight permits, fuel permits, or operating accounts that apply when a truck enters or operates in that state.',
            'The best time to review state permit needs is before a carrier accepts a load. Waiting until the truck is near the state line creates stress and can lead to delays, penalties, or missed pickup windows. Dispatchers, owner-operators, and fleet managers should maintain a permit checklist for regular lanes.'
          ]
        },
        {
          heading: 'Common state permit searches',
          paragraphs: [
            'Carriers frequently search for KYU number, New York HUT, New Mexico WDT, Oregon permit, Connecticut highway use tax, California permits, temporary trip permits, oversize permits, and fuel permits. Not every carrier needs every item, but each should be checked against the route and vehicle profile.',
            'For example, a carrier running Midwest to Northeast lanes may need to understand New York HUT. A carrier entering New Mexico may need weight distance tax account guidance. A carrier operating in Oregon may need Oregon weight-mile or related permit review. The correct answer is lane-specific, not generic.'
          ]
        },
        {
          heading: 'Trip permits and temporary permits',
          paragraphs: [
            'Temporary permits are often used when a carrier does not have a permanent credential for a specific state or trip. These permits can be time-sensitive and information-sensitive. The carrier may need unit details, VIN, plate, weight, route, origin, destination, dates, and insurance information.',
            'A temporary permit should not be treated as an afterthought. If the route changes, the vehicle changes, or the load timing changes, the permit may need review. Clear dispatch communication helps avoid mistakes.'
          ]
        },
        {
          heading: 'How to plan state permit coverage',
          paragraphs: [
            'A practical permit plan starts with the lanes you expect to run. List the states, vehicle weights, cargo type, authority status, IFTA and IRP status, and any state accounts already active. Then identify gaps before the truck moves.',
            'Best Truck Permits helps carriers review state permit needs for common freight corridors and prepare requests for KYU, NY HUT, NM WDT, Oregon, trip permits, and other state filings. The goal is to reduce last-minute confusion and keep dispatch decisions aligned with permit readiness.'
          ]
        }
      ],
      checklist: ['Route states and expected lanes', 'Vehicle weight and axle details', 'USDOT and MC information', 'IFTA and IRP status', 'Existing state account numbers', 'Load dates and trip details', 'Insurance and carrier contact information'],
      questions: [
        { question: 'Do I need state permits if I have IFTA and IRP?', answer: 'Possibly. IFTA and IRP are important interstate credentials, but some states still require additional mileage tax, highway use, or temporary permits.' },
        { question: 'Can state permits be handled same day?', answer: 'Some requests may begin quickly when information is complete, but timing depends on the state, permit type, payment, and document accuracy.' },
        { question: 'Should dispatch check permits before booking?', answer: 'Yes. Dispatch should confirm route permit needs before committing to a load that enters a state with special requirements.' }
      ],
      keywords: ['state truck permits', 'KYU number', 'New York HUT', 'New Mexico WDT', 'Oregon permit', 'temporary trip permit', 'truck permit company'],
      finalNote: 'State permit planning is one of the clearest ways to protect time, avoid confusion, and keep trucks moving across high-requirement jurisdictions.'
    })
  },
  {
    id: 'dispatch-paperwork-factoring-ready',
    title: 'Dispatch Paperwork and Factoring Readiness for New Trucking Companies',
    category: 'Dispatch',
    date: '2026-02-18',
    imageUrl: '/assets/blog-dispatch-paperwork-factoring-carrier-packet.jpg',
    excerpt: 'Learn what documents help a new carrier become broker-ready, dispatch-ready, and factoring-ready.',
    content: articleHtml({
      intro: 'A new trucking company is not fully ready just because the authority application has been filed. Brokers, dispatchers, factoring companies, insurance agents, and shippers may all ask for documents before the first load is booked or paid. This guide explains how dispatch paperwork and factoring readiness fit into the bigger startup plan.',
      sections: [
        {
          heading: 'What broker-ready paperwork means',
          paragraphs: [
            'Broker-ready paperwork usually includes a W-9, certificate of insurance, operating authority, carrier profile, equipment details, safety contact information, factoring notice if applicable, voided check or payment details, and signed carrier agreements. Some brokers request additional safety or compliance documents, especially for new authorities.',
            'If a carrier waits to gather these documents until a dispatcher finds a load, the setup process can slow down. A broker may move on to another truck if the packet is incomplete. That is why carrier packet preparation should happen while permits and authority items are being finalized.'
          ]
        },
        {
          heading: 'How factoring helps cash flow',
          paragraphs: [
            'Factoring can help carriers receive payment faster after delivering a load. Instead of waiting on a broker or shipper payment cycle, the carrier sells the invoice to a factoring company under agreed terms. Rates, reserves, recourse terms, credit checks, and paperwork requirements vary, so carriers should review options carefully.',
            'Best Truck Permits helps new trucking companies prepare the documents commonly requested during factoring setup. We do not promise approval or a specific percentage, but we help carriers organize the information factoring companies typically review.'
          ]
        },
        {
          heading: 'Dispatch readiness and load communication',
          paragraphs: [
            'Dispatch readiness means the carrier has authority status, insurance, equipment details, lane preferences, rate expectations, factoring details, and paperwork ready before load calls begin. Good dispatch communication reduces confusion about pickup times, delivery times, detention, layover, commodity, weight, equipment type, and broker requirements.',
            'Carriers that need load searching and broker communication can also review dispatch support resources at <a href="https://www.besttruckdispatch.com" target="_blank" rel="noreferrer">www.besttruckdispatch.com</a>. Permit readiness and dispatch readiness work best together because a strong load plan depends on accurate authority, insurance, and permit information.'
          ]
        },
        {
          heading: 'Avoiding first-load delays',
          paragraphs: [
            'First-load delays often happen because a carrier has one missing document, a mismatch between business names, an inactive authority status, an insurance filing problem, or an unclear factoring notice. These problems can be avoided with a clean checklist and a single folder for all carrier documents.',
            'A new company should also keep copies of permits, receipts, filing confirmations, and renewal reminders. Trucking is document-heavy, and organized records make broker setup, factoring, audits, renewals, and dispatch communication easier.'
          ]
        }
      ],
      checklist: ['W-9 and business details', 'Certificate of insurance', 'Operating authority status', 'USDOT and MC details', 'Carrier profile and equipment list', 'Factoring notice or payment instructions', 'Voided check if required', 'Permit and registration credentials', 'Emergency contact and dispatch contact'],
      questions: [
        { question: 'Can a dispatcher book loads before authority is active?', answer: 'A dispatcher may research lanes, but a carrier should not accept regulated freight until authority, insurance, permits, and broker requirements are ready.' },
        { question: 'Is factoring required?', answer: 'No, but many carriers use factoring to improve cash flow. The right choice depends on rates, payment needs, customer credit, and business goals.' },
        { question: 'What is the fastest way to become broker-ready?', answer: 'Prepare the carrier packet early, confirm authority status, keep insurance documents current, and make sure permit and registration details are accurate.' }
      ],
      keywords: ['truck dispatch paperwork', 'carrier packet setup', 'factoring setup for trucking', 'new authority dispatch', 'broker setup documents', 'truck dispatch service'],
      finalNote: 'Best Truck Permits helps carriers connect the dots between permits, authority, factoring readiness, and dispatch paperwork so the company is better prepared for real freight conversations.'
    })
  }
];

const faqs = [
  {
    question: 'How fast can truck permits be processed?',
    answer: 'Many filings can begin the same day once documents are ready. Processing times depend on the permit type, agency rules, and whether information is complete.'
  },
  {
    question: 'Do you provide truck permit services nationwide?',
    answer: 'Yes. Best Truck Permits supports carriers and owner-operators across the United States with federal, state, and interstate permit needs.'
  },
  {
    question: 'Can you help with DOT and MC compliance?',
    answer: 'Yes. The team supports USDOT, MC authority, BOC-3, UCR, IFTA, IRP, and related trucking compliance workflows.'
  },
  {
    question: 'Do new authority carriers need BOC-3?',
    answer: 'Yes. BOC-3 filing is required before many motor carrier authorities can become active.'
  },
  {
    question: 'Can Best Truck Permits help me start a new trucking company?',
    answer: 'Yes. We help new carriers understand business setup, EIN readiness, USDOT and MC authority, BOC-3, UCR, IFTA, IRP, state permits, and the paperwork commonly needed before dispatch and broker setup.'
  },
  {
    question: 'What is the difference between USDOT and MC authority?',
    answer: 'A USDOT number identifies a motor carrier for safety and compliance tracking. MC authority is operating authority for certain for-hire interstate carriers. Many trucking companies need both before hauling regulated freight across state lines.'
  },
  {
    question: 'Do I need IFTA and IRP for interstate trucking?',
    answer: 'Many interstate carriers need IFTA for fuel tax reporting and IRP for apportioned plates. Requirements depend on vehicle weight, jurisdictions traveled, and operation type.'
  },
  {
    question: 'Can you help with state permits like KYU, NY HUT, NM WDT, and Oregon permits?',
    answer: 'Yes. We help carriers identify and prepare state-specific operating permits, mileage tax accounts, renewals, and filing support for key trucking jurisdictions.'
  },
  {
    question: 'Do you help with factoring setup for new trucking companies?',
    answer: 'Yes. We help new carriers prepare documents and connect with factoring options so they can improve cash flow after booking loads.'
  },
  {
    question: 'What documents should I prepare before requesting permit help?',
    answer: 'Helpful documents include business details, EIN, owner information, vehicle details, insurance information, address proof, prior authority information if available, and any state or federal notices you received.'
  },
  {
    question: 'Can you support owner-operators and fleets in multiple states?',
    answer: 'Yes. Best Truck Permits supports owner-operators, dispatch-ready carriers, and fleets across the United States with federal filings, state permits, registration guidance, and renewal support.'
  },
  {
    question: 'What is BOC-3 and why is it important?',
    answer: 'BOC-3 is a process agent filing connected to many FMCSA operating authorities. Missing or incorrect BOC-3 filing can delay authority activation for new for-hire interstate carriers.'
  },
  {
    question: 'Can you help with IRP plates and apportioned registration?',
    answer: 'Yes. We help carriers prepare IRP plate and apportioned registration requests by organizing vehicle details, ownership records, insurance information, Form 2290 proof when required, and mileage information.'
  },
  {
    question: 'What is UCR registration?',
    answer: 'Unified Carrier Registration is required for many interstate carriers and related transportation businesses. We help carriers understand whether UCR applies and prepare renewal or registration steps.'
  },
  {
    question: 'Do I need a KYU number?',
    answer: 'A KYU number may apply to certain carriers operating qualifying commercial vehicles in Kentucky. Requirements depend on vehicle weight, operation type, and route.'
  },
  {
    question: 'Do I need New York HUT?',
    answer: 'New York Highway Use Tax may apply to certain carriers operating in New York. We help carriers review whether New York HUT should be part of the state permit plan.'
  },
  {
    question: 'What is New Mexico WDT?',
    answer: 'New Mexico Weight Distance Tax can apply to certain commercial vehicles operating in New Mexico. Carriers should review route, weight, and account requirements before entering the state.'
  },
  {
    question: 'Can you help with Oregon truck permits?',
    answer: 'Yes. We support Oregon permit review and related weight-mile or trip permit preparation based on the carrier route, vehicle, and account status.'
  },
  {
    question: 'Do you help California trucking companies?',
    answer: 'Yes. We help California carriers and carriers entering California with permit planning, company registration support, IFTA, IRP, MCP-related readiness, CA number review, and other paperwork needs.'
  },
  {
    question: 'Do you help Texas trucking companies?',
    answer: 'Yes. We support Texas owner-operators and fleets with new authority setup, USDOT and MC filings, IFTA, IRP, UCR, and interstate permit planning.'
  },
  {
    question: 'Do you help New York trucking companies?',
    answer: 'Yes. We help carriers in New York and carriers entering New York review authority, IFTA, IRP, UCR, New York HUT, and related permit needs.'
  },
  {
    question: 'Can you help me prepare a carrier packet?',
    answer: 'Yes. We help carriers understand common broker setup documents such as W-9, certificate of insurance, authority records, equipment details, factoring notice, and contact information.'
  },
  {
    question: 'Can you help with dispatch paperwork?',
    answer: 'Yes. Our dispatch-related support focuses on paperwork readiness, load communication details, carrier packet preparation, invoicing preparation, and factoring coordination.'
  },
  {
    question: 'Can you help with factoring setup?',
    answer: 'Yes. We help new trucking companies prepare common documents needed to speak with factoring companies and compare factoring options. Final approval and rates are determined by the factoring provider.'
  },
  {
    question: 'Are Best Truck Permits fees separate from government fees?',
    answer: 'Yes. Best Truck Permits charges service fees for administrative support, document preparation, filing guidance, and follow-up. Government, agency, state, third-party, or payment processor fees may be separate unless clearly included in a package.'
  },
  {
    question: 'Can you guarantee an agency approval?',
    answer: 'No. We help prepare and support filings, but government agencies, insurers, factoring companies, brokers, and third parties control their own approval decisions, timelines, and requirements.'
  },
  {
    question: 'How do I request help?',
    answer: `Call ${phone}, use the contact form, or send the WhatsApp message from the website. Include your company name, service needed, state, vehicle details, and any deadline so our team can review next steps.`
  }
];

const sliderCards = [
  {
    image: '/assets/slider-registration.jpg',
    title: 'New Authority Setup',
    text: 'Company registration, DOT, MC, BOC-3, UCR and startup paperwork.'
  },
  {
    image: '/assets/slider-permits.jpg',
    title: 'IFTA, IRP & State Permits',
    text: 'Fuel tax, apportioned plates, cab cards, renewals and trip permits.'
  },
  {
    image: '/assets/permit-compliance-desk.jpg',
    title: 'Carrier Compliance Desk',
    text: 'Organized filings, reminders, document review and permit guidance.'
  }
];

const packages = [
  {
    name: 'INC Filing Package',
    productId: 'pro_01kv7t5mh44g30xcf99p1f3vq7',
    priceId: 'pri_01kv7t7yzb6sb8ytdm5v9qxa7w',
    oldPrice: '$1500',
    price: '$1200',
    note: 'one-time setup',
    summary: 'A complete new trucking company registration package for carriers who need authority, tax, and permit foundations handled together.',
    featured: false,
    features: ['INC & LLC filing', 'Federal Tax ID', 'USDOT/MC Number', 'BOC-3 filing', 'UCR', 'S Corp filing', 'CA Number', 'IFTA CA', 'Statement of Information', 'New Mexico WDT', 'Oregon Permit guidance', 'NY HUT Permit', 'KYU Number']
  },
  {
    name: 'Annual Advance Agent Service Plan',
    productId: 'pro_01kv7tae1y48tb067x73d1f6xh',
    priceId: 'pri_01kv7tc3hh7gnvej51cqytwh3g',
    oldPrice: '$450',
    price: '$300',
    note: 'per truck annually',
    summary: 'Recurring permit and renewal support for carriers who want fewer compliance surprises throughout the year.',
    featured: true,
    features: ['IRP license plate renewal', '2290 tax filing', 'IFTA CA quarterly fuel tax filing', 'USDOT update', 'MC number update', 'UCR registration', 'Kentucky mileage updates', 'New York HUT filing', 'Oregon monthly tax filing', 'CARB certificate renewal']
  },
  {
    name: 'Essential Package',
    productId: 'pro_01kv7tf71160c7kk2s7ydvxveb',
    priceId: 'pri_01kv7thejgamh5qk76xsfrj877',
    oldPrice: '$350',
    price: '$250',
    note: 'per truck annually',
    summary: 'A lean compliance package for owner-operators and small fleets running key western and regional routes.',
    featured: false,
    features: ['UCR update and renewal', 'IFTA California permit renewal', 'New Mexico WDT renewal', 'IFTA quarterly fuel tax filing', 'New Mexico quarterly filing', 'Kentucky mileage update', 'CARB certificate', 'CA and MCP number update', 'Clean Truck Check support']
  },
  {
    name: 'Advantage Package',
    productId: 'pro_01kv7tksdhskfh69gqshwngk2m',
    priceId: 'pri_01kv7tn9q96aj647yq1t9as3w1',
    oldPrice: '$450',
    price: '$300',
    note: 'per truck annually',
    summary: 'A stronger package for active interstate carriers that need annual and quarterly permit coverage.',
    featured: false,
    features: ['Everything in Essential', 'IRP license plate renewal', 'New York HUT permit renewal', 'Oregon bond renewal update', 'Connecticut highway use tax filing', 'Oregon monthly tax filing', 'Arkansas carrier annual report', '2290 heavy vehicle use tax filing', 'USDOT number update']
  },
  {
    name: 'Premium Package',
    productId: 'pro_01kv7tpp94snedta3dq3a7nm2r',
    priceId: 'pri_01kv7trh1jkwzm87bzr8577ym1',
    oldPrice: '$550',
    price: '$400',
    note: 'per truck annually',
    summary: 'A proactive compliance package for larger fleets needing deeper reporting and performance oversight.',
    featured: false,
    features: ['Everything in Essential and Advantage', 'DataQs filing support', 'Quarterly SAFER scoring evaluation report', 'Driver vehicle inspection report review', 'Quarterly vehicle maintenance report', 'Fleet compliance check-ins']
  },
  {
    name: 'Dispatch Growth Plan',
    productId: 'pro_01kv7yqcq2xah379hb7hpqky9t',
    priceId: 'pri_01kv7yrz12y80af366t1pryqn6',
    oldPrice: '',
    price: 'Fixed Fee',
    note: 'dispatch support package',
    summary: 'A fixed-fee dispatch support package for carriers that want help finding loads, negotiating rates, and keeping paperwork organized. You can opt into a percentage-based dispatch package later, and our team can update your package on request.',
    featured: false,
    features: ['Load searching support', 'Rate negotiation assistance', 'Broker communication', 'Paperwork handling', 'Carrier packet coordination', 'Invoicing support', 'Dispatch-ready document organization', 'Option to move to percentage-based dispatch later']
  }
];

const stateMarkets = [
  { state: 'California', slug: 'california', focus: 'CA Number, MCP, IFTA California, IRP plates, CARB-related paperwork, and permits for carriers operating from ports, farms, warehouses, and cross-state freight lanes.' },
  { state: 'Texas', slug: 'texas', focus: 'new authority setup, USDOT and MC filings, apportioned registration, IFTA, UCR, and permits for carriers serving major freight corridors in Dallas, Houston, San Antonio, Austin, and border lanes.' },
  { state: 'New York', slug: 'new-york', focus: 'New York HUT, interstate authority, UCR, IFTA, IRP, trip planning, and carrier paperwork for fleets entering or operating in New York.' },
  { state: 'Florida', slug: 'florida', focus: 'USDOT and MC authority, IFTA, IRP, UCR, trip permits, and new trucking company setup for carriers moving freight across Southeast lanes.' },
  { state: 'Georgia', slug: 'georgia', focus: 'authority registration, IFTA, IRP, UCR, state permit review, and carrier compliance support for Atlanta-area and regional freight operations.' },
  { state: 'Illinois', slug: 'illinois', focus: 'MC authority, UCR, IFTA, IRP, apportioned plates, and trucking permits for carriers operating through Chicago and Midwest freight networks.' },
  { state: 'Ohio', slug: 'ohio', focus: 'DOT and MC registration, IFTA, IRP, UCR, temporary permits, and compliance support for carriers serving Midwest and Northeast lanes.' },
  { state: 'Pennsylvania', slug: 'pennsylvania', focus: 'new trucking company registration, authority filing, IFTA, IRP, UCR, and permit planning for carriers crossing Pennsylvania freight routes.' }
];

const supportScenarios = [
  {
    title: 'New authority startup',
    label: 'DOT, MC, BOC-3, UCR',
    image: '/assets/hero-consultant.jpg',
    text: 'Support for carriers preparing new trucking company paperwork, authority filing details, and first-load readiness.'
  },
  {
    title: 'Permit renewal planning',
    label: 'IFTA, IRP, UCR, state filings',
    image: '/assets/slider-registration.jpg',
    text: 'Guidance for interstate carriers organizing renewal documents, mileage records, apportioned plate details, and fuel tax reporting.'
  },
  {
    title: 'State permit research',
    label: 'KYU, NY HUT, NM WDT, Oregon',
    image: '/assets/slider-registration.jpg',
    text: 'Practical help identifying state-specific permit requirements before a truck enters a regulated jurisdiction.'
  },
  {
    title: 'Carrier packet readiness',
    label: 'Dispatch and broker paperwork',
    image: '/assets/hero-consultant.jpg',
    text: 'Document preparation support for carriers who need cleaner paperwork before broker setup, dispatch, or factoring review.'
  },
  {
    title: 'Factoring preparation',
    label: 'Cash-flow setup support',
    image: '/assets/hero-consultant.jpg',
    text: 'Help gathering the business, authority, insurance, and invoice documents factoring companies commonly request.'
  },
  {
    title: 'Fleet compliance check-ins',
    label: 'Annual permit organization',
    image: '/assets/slider-registration.jpg',
    text: 'Structured reminders and document guidance for fleets tracking annual renewals, state filings, and recurring compliance tasks.'
  }
];

let paddleScriptPromise;
let paddleConfigPromise;
let paddleInitialized = false;

function loadPaddleConfig() {
  if (window.BTP_PADDLE) return Promise.resolve(window.BTP_PADDLE);
  if (!paddleConfigPromise) {
    paddleConfigPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = '/paddle-config.js';
      script.async = true;
      script.onload = () => resolve(window.BTP_PADDLE || {});
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }
  return paddleConfigPromise;
}

function loadPaddleScript() {
  if (typeof window === 'undefined') return Promise.reject(new Error('Paddle checkout is only available in the browser.'));
  if (window.Paddle) return Promise.resolve(window.Paddle);
  if (!paddleScriptPromise) {
    paddleScriptPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[src="https://cdn.paddle.com/paddle/v2/paddle.js"]');
      if (existing) {
        existing.addEventListener('load', () => resolve(window.Paddle), { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
      script.async = true;
      script.onload = () => resolve(window.Paddle);
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }
  return paddleScriptPromise;
}

async function openPaddleCheckout(packageItem) {
  await loadPaddleConfig();
  const config = window.BTP_PADDLE || {};
  const clientToken = String(config.clientToken || '').trim();

  if (!packageItem?.priceId) {
    alert('This package is missing a Paddle Price ID. Please contact Best Truck Permits.');
    return;
  }

  if (!clientToken) {
    alert('Paddle checkout is not configured yet. Add your Paddle client-side token in paddle-config.js, then try again.');
    return;
  }

  try {
    const Paddle = await loadPaddleScript();
    if (!Paddle) throw new Error('Paddle script did not load.');

    const environment = String(config.environment || 'production').toLowerCase();
    if (environment && environment !== 'production' && Paddle.Environment?.set) {
      Paddle.Environment.set(environment);
    }

    if (!paddleInitialized) {
      Paddle.Initialize({
        token: clientToken,
        checkout: {
          settings: {
            displayMode: 'overlay',
            theme: 'light',
            locale: 'en'
          }
        }
      });
      paddleInitialized = true;
    }

    Paddle.Checkout.open({
      items: [{ priceId: packageItem.priceId, quantity: 1 }],
      customData: {
        package_name: packageItem.name,
        product_id: packageItem.productId
      },
      settings: {
        displayMode: 'overlay',
        theme: 'light',
        locale: 'en',
        successUrl: `${window.location.origin}/packages?checkout=success`
      }
    });
  } catch (error) {
    console.error(error);
    alert('Paddle checkout could not be opened. Please refresh the page or contact Best Truck Permits.');
  }
}

const metaMap = {
  '/': {
    title: 'Best Truck Permits | Truck Permits, DOT, MC, IFTA, IRP & Dispatch',
    description: 'Nationwide truck permits, DOT and MC registration, BOC-3, IFTA, IRP, UCR, state permits, and dispatch services for carriers across the USA.'
  },
  '/services': {
    title: 'Truck Permit Services in the USA | DOT, MC, IFTA, IRP & UCR',
    description: 'Explore truck permit services including company registration, USDOT, MC, BOC-3, IFTA, IRP plates, UCR, state permits, and dispatch support.'
  },
  '/about': {
    title: 'About Best Truck Permits | Nationwide Truck Permit Company',
    description: 'Learn about Best Truck Permits, a nationwide truck permit company helping carriers with DOT, MC, BOC-3, IFTA, IRP, UCR, state permits, registrations, and factoring setup.'
  },
  '/faq': {
    title: 'Truck Permit FAQ | DOT, MC, BOC-3, IFTA, IRP & State Permits',
    description: 'Answers to common truck permit questions about USDOT, MC authority, BOC-3, IFTA, IRP plates, UCR, state permits, new authority setup, and factoring.'
  },
  '/factoring': {
    title: 'Truck Factoring Setup for New Trucking Companies | Best Truck Permits',
    description: 'Best Truck Permits helps new trucking companies prepare documents and connect with factoring options for better cash flow and competitive factoring percentages.'
  },
  '/blog': {
    title: 'Truck Permit Guides | USDOT, MC, IFTA, IRP & State Permits',
    description: 'Read practical guides about USDOT and MC registration, BOC-3, IFTA, IRP, UCR, state permits, and carrier paperwork.'
  },
  '/contact': {
    title: 'Contact Best Truck Permits | Get a Truck Permit Quote',
    description: 'Request help with truck permits, DOT and MC registration, IFTA, IRP, UCR, BOC-3, state permits, or dispatch services.'
  },
  '/packages': {
    title: 'Truck Permit Packages & Pricing | Best Truck Permits',
    description: 'Compare truck permit packages for new trucking company setup, annual permit renewals, IFTA, IRP, UCR, state permits, and fleet compliance support.'
  },
  '/terms-of-service': {
    title: 'Terms of Service | Best Truck Permits Truck Permit Services',
    description: 'Read the Best Truck Permits terms of service from LoadaiX for paid truck permit services, DOT and MC registration support, IFTA, IRP, UCR, state permits, dispatch paperwork, and factoring setup assistance.'
  },
  '/privacy-policy': {
    title: 'Privacy Policy | Best Truck Permits Customer Information',
    description: 'Learn how Best Truck Permits collects, uses, stores, and protects customer information for truck permits, DOT and MC registration, IFTA, IRP, UCR, state filings, quotes, and support requests.'
  },
  '/refund-policy': {
    title: 'Refund Policy | Best Truck Permits Paid Service Fees',
    description: 'Review the Best Truck Permits refund policy for Paddle payments, paid truck permit services, agency filing support, administrative work, package fees, and customer-requested trucking compliance services.'
  },
  '/disclaimer': {
    title: 'Service Disclaimer | Best Truck Permits',
    description: 'Read the Best Truck Permits disclaimer for trucking permit information, agency requirements, filing timelines, third-party fees, and paid administrative support.'
  },
  '/editorial-policy': {
    title: 'Editorial Policy | Best Truck Permits',
    description: 'Learn how Best Truck Permits creates and reviews educational content about DOT, MC, BOC-3, IFTA, IRP, UCR, state truck permits, factoring, and dispatch paperwork.'
  },
  '/corrections-policy': {
    title: 'Corrections Policy | Best Truck Permits',
    description: 'Learn how Best Truck Permits reviews and corrects website information about truck permits, authority registration, filing requirements, pricing, and policy content.'
  },
  '/cookie-policy': {
    title: 'Cookie Policy | Best Truck Permits',
    description: 'Learn how Best Truck Permits may use cookies, browser storage, analytics, and server logs to operate the website and improve trucking permit service content.'
  },
  '/admin/login': {
    title: 'Admin Login | Best Truck Permits',
    description: 'Admin login for Best Truck Permits.'
  },
  '/admin-login': {
    title: 'Admin Login | Best Truck Permits',
    description: 'Admin login for Best Truck Permits.'
  },
  '/admin': {
    title: 'Admin | Best Truck Permits',
    description: 'View form submissions and manage blog posts for Best Truck Permits.'
  }
};

function getPath() {
  const current = window.location.pathname === '' ? '/' : window.location.pathname;
  if (current.length > 1 && current.endsWith('/')) return current.slice(0, -1);
  return current;
}

function useRoute() {
  const [path, setPath] = useState(getPath());

  useEffect(() => {
    const onPop = () => setPath(getPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = (nextPath) => {
    window.history.pushState({}, '', nextPath);
    setPath(nextPath);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return [path, navigate];
}

function useLocalState(key, fallback) {
  const [value, setValue] = useState(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : fallback;
    } catch {
      return fallback;
    }
  });

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);

  return [value, setValue];
}

async function apiRequest(endpoint, options = {}) {
  const response = await fetch(`/api/${endpoint}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {})
    },
    ...options
  });
  const raw = await response.text();
  let data = {};
  try {
    data = raw ? JSON.parse(raw) : {};
  } catch {
    data = {};
  }
  if (!response.ok || data.ok === false) {
    throw new Error(data.error || `Request failed. Server returned HTTP ${response.status}.`);
  }
  return data;
}

async function uploadBlogImage(file) {
  const form = new FormData();
  form.append('image', file);
  const response = await fetch('/api/upload.php', {
    method: 'POST',
    credentials: 'include',
    body: form
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.ok === false) {
    throw new Error(data.error || 'Image upload failed');
  }
  return data.url;
}

function normalizePost(post) {
  return {
    ...post,
    imageUrl: post.imageUrl || post.image_url || '/assets/permit-compliance-desk.jpg',
    createdAt: post.createdAt || post.created_at || post.date
  };
}

function mergePosts(remotePosts) {
  const normalized = (remotePosts || []).map(normalizePost);
  const remoteById = new Map(normalized.map((post) => [post.id, post]));
  const evergreenPosts = seedPosts.map((seed) => (
    remoteById.has(seed.id)
      ? { ...remoteById.get(seed.id), ...seed }
      : seed
  ));
  const customPosts = normalized.filter((post) => !seedPosts.some((seed) => seed.id === post.id));
  return [...evergreenPosts, ...customPosts];
}

function setMeta(path) {
  const service = services.find((item) => path === `/services/${item.slug}`);
  const post = seedPosts.find((item) => path === `/blog/${item.id}`);
  const companyState = stateMarkets.find((item) => path === `/trucking-company-in-${item.slug}`);
  const permitState = stateMarkets.find((item) => path === `/truck-permits-in-${item.slug}`);
  const meta = service
    ? {
        title: `${service.title} | Best Truck Permits`,
        description: `${service.summary} Get a document checklist and filing guidance from Best Truck Permits.`
      }
    : companyState
      ? {
          title: `Trucking Company in ${companyState.state} | Registration & Authority Setup`,
          description: `Start or register a trucking company in ${companyState.state} with help for USDOT, MC authority, BOC-3, UCR, IFTA, IRP, state permits, and carrier paperwork.`
        }
      : permitState
        ? {
            title: `Truck Permits in ${permitState.state} | DOT, MC, IFTA, IRP & State Filings`,
            description: `Get truck permit help in ${permitState.state} for DOT, MC, BOC-3, UCR, IFTA, IRP, trip permits, renewals, and state-specific compliance filings.`
          }
    : post
      ? {
          title: `${post.title} | Best Truck Permits Blog`,
          description: post.excerpt
        }
      : metaMap[path] || metaMap['/'];
  document.title = meta.title;
  const description = document.querySelector('meta[name="description"]');
  if (description) description.setAttribute('content', meta.description);
  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) canonical.setAttribute('href', `https://besttruckpermits.com${path === '/' ? '/' : path}`);
  const ogTitle = document.querySelector('meta[property="og:title"]');
  if (ogTitle) ogTitle.setAttribute('content', meta.title);
  const ogDescription = document.querySelector('meta[property="og:description"]');
  if (ogDescription) ogDescription.setAttribute('content', meta.description);
  const ogUrl = document.querySelector('meta[property="og:url"]');
  if (ogUrl) ogUrl.setAttribute('content', `https://besttruckpermits.com${path === '/' ? '/' : path}`);
}

function Header({ path, navigate, setSubmissions }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [servicesMobileOpen, setServicesMobileOpen] = useState(false);
  const [stickyVisible, setStickyVisible] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [connectSent, setConnectSent] = useState(false);
  const [connectForm, setConnectForm] = useState({
    name: '',
    phone: '',
    email: '',
    service: 'New Trucking Company Registration',
    message: ''
  });
  const isHome = path === '/';

  useEffect(() => {
    let lastScrollY = window.scrollY || 0;
    let ticking = false;

    const updateHeader = () => {
      const currentY = window.scrollY || 0;
      const scrollingUp = currentY < lastScrollY - 8;
      const scrollingDown = currentY > lastScrollY + 8;

      if (currentY < 150 || scrollingDown) {
        setStickyVisible(false);
      } else if (currentY > 220 && scrollingUp) {
        setStickyVisible(true);
      }

      lastScrollY = currentY;
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(updateHeader);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (nextPath) => {
    setDrawerOpen(false);
    setMobileNavOpen(false);
    setServicesMobileOpen(false);
    navigate(nextPath);
  };
  const updateConnect = (field, value) => setConnectForm((current) => ({ ...current, [field]: value }));
  const submitConnect = async (event) => {
    event.preventDefault();
    const entry = {
      id: crypto.randomUUID(),
      ...connectForm,
      source: "Let's connect",
      createdAt: new Date().toISOString()
    };
    try {
      await apiRequest('submissions.php', {
        method: 'POST',
        body: JSON.stringify(entry)
      });
    } catch {
      // Local fallback keeps preview usable before PHP/MySQL is configured.
    }
    if (setSubmissions) {
      setSubmissions((current) => [entry, ...current]);
    }
    setConnectForm({ name: '', phone: '', email: '', service: 'New Trucking Company Registration', message: '' });
    setConnectSent(true);
  };

  return (
    <header className={`${isHome ? 'header-wrap home-header' : 'header-wrap inner-header'}${stickyVisible ? ' sticky-visible' : ''}${mobileNavOpen ? ' menu-open' : ''}`}>
      <div className="site-header">
        <a className="brand" href="/" onClick={(event) => { event.preventDefault(); go('/'); }} aria-label="Best Truck Permits home">
          <SiteImage src={brandLogo} alt="Best Truck Permits & Dispatch" />
        </a>
        <button className={mobileNavOpen ? 'mobile-menu-toggle open' : 'mobile-menu-toggle'} type="button" aria-label={mobileNavOpen ? 'Close navigation menu' : 'Open navigation menu'} aria-expanded={mobileNavOpen} onClick={() => setMobileNavOpen((open) => !open)}>
          <span></span>
          <span></span>
          <span></span>
        </button>
        <nav className={mobileNavOpen ? 'nav open' : 'nav'} aria-label="Primary navigation">
          {navItems.map((item) => {
            if (item.path === '/services') {
              return (
                <div className={servicesMobileOpen ? 'nav-dropdown open' : 'nav-dropdown'} key={item.path}>
                  <a
                    className={path === item.path || path.startsWith('/services/') ? 'active' : ''}
                    href={item.path}
                    aria-expanded={servicesMobileOpen}
                    onClick={(event) => {
                      event.preventDefault();
                      if (window.matchMedia('(max-width: 1180px)').matches) {
                        setServicesMobileOpen((open) => !open);
                        return;
                      }
                      go(item.path);
                    }}
                  >
                    Services <span className="nav-chevron" aria-hidden="true"></span>
                  </a>
                  <div className="nav-dropdown-menu">
                    <button type="button" onClick={() => go('/services')}>
                      <strong>All Services</strong>
                      <small>View every truck permit service</small>
                    </button>
                    {services.map((service) => (
                      <button type="button" key={service.slug} onClick={() => go(`/services/${service.slug}`)}>
                        <strong>{service.title}</strong>
                        <small>{service.label}</small>
                      </button>
                    ))}
                  </div>
                </div>
              );
            }

            return (
              <a
                key={item.path}
                className={
                  item.path === '/admin-login'
                    ? (path === '/admin-login' || path === '/admin' ? 'active' : '')
                    : (path === item.path || (item.path !== '/' && path.startsWith(`${item.path}/`)) ? 'active' : '')
                }
                href={item.path}
                onClick={(event) => {
                  event.preventDefault();
                  go(item.path);
                }}
              >
                {item.label}
              </a>
            );
          })}
          <div className="mobile-nav-actions">
            <button type="button" onClick={() => { setMobileNavOpen(false); setConnectOpen(true); setConnectSent(false); }}>Get Quote <span aria-hidden="true">→</span></button>
          </div>
        </nav>
        <button className="header-phone" type="button" aria-label="Get a quote" onClick={() => { setMobileNavOpen(false); setConnectOpen(true); setConnectSent(false); }}>
          <span className="header-phone-label">Get Quote</span>
          <ArrowIcon />
        </button>
        <a className="nav-message" href={navigationWhatsappUrl} target="_blank" rel="noreferrer" aria-label="Message Best Truck Permits on WhatsApp" title="Message us on WhatsApp"><span aria-hidden="true">WA</span></a>
        <button className="menu-toggle desktop-drawer-toggle" type="button" aria-label="Open menu" aria-expanded={drawerOpen} onClick={() => setDrawerOpen(true)}>
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
      {drawerOpen ? (
        <>
          <div className="drawer-backdrop open" onClick={() => setDrawerOpen(false)}></div>
          <aside className="side-drawer open" aria-label="Services and contact menu">
            <div className="drawer-heading">
              <SiteImage src={brandLogo} alt="Best Truck Permits & Dispatch" />
              <button type="button" aria-label="Close menu" onClick={() => setDrawerOpen(false)}><span className="close-icon" aria-hidden="true"></span></button>
            </div>
            <p>Nationwide truck permit, authority, registration, and dispatch support for owner-operators and fleets.</p>
            <div className="drawer-search">
              <input id="drawer-service-search" aria-label="Search services" placeholder="Search services..." />
              <button type="button" aria-label="Search"><span className="search-icon" aria-hidden="true"></span></button>
            </div>
            <div className="drawer-block">
              <h2>Services</h2>
              {services.map((service) => (
                <button type="button" key={service.slug} onClick={() => go(`/services/${service.slug}`)}>{service.title}</button>
              ))}
            </div>
            <div className="drawer-block">
              <h2>Contact Info</h2>
              <span>Phone</span>
              <a href={`tel:${phoneHref}`}>{phone}</a>
              <span>Email</span>
              <a href={`mailto:${email}`}>{email}</a>
              <span>Location</span>
              <p>4460 W Shaw Ave, Fresno, CA 93722</p>
            </div>
          </aside>
        </>
      ) : null}
      {connectOpen ? <div className="connect-modal open" role="dialog" aria-modal="true" aria-labelledby="connect-modal-title">
        <div className="connect-modal-card">
          <button className="connect-close" type="button" aria-label="Close form" onClick={() => setConnectOpen(false)}><span className="close-icon" aria-hidden="true"></span></button>
          <span className="eyebrow">Get a quote</span>
          <h2 id="connect-modal-title">Request truck permit help</h2>
          <p>Send your details and our admin dashboard will save your request for follow-up.</p>
          <form onSubmit={submitConnect}>
            {connectSent ? <p className="success">Thanks. Your request has been sent to admin.</p> : null}
            <label>
              Full name
              <input value={connectForm.name} onChange={(event) => updateConnect('name', event.target.value)} required />
            </label>
            <label>
              Phone
              <input value={connectForm.phone} onChange={(event) => updateConnect('phone', event.target.value)} required />
            </label>
            <label>
              Email
              <input type="email" value={connectForm.email} onChange={(event) => updateConnect('email', event.target.value)} required />
            </label>
            <label>
              Service
              <select value={connectForm.service} onChange={(event) => updateConnect('service', event.target.value)}>
                {services.map((service) => <option key={service.slug}>{service.title}</option>)}
                <option>Factoring Setup</option>
              </select>
            </label>
            <label>
              Message
              <textarea rows="4" value={connectForm.message} onChange={(event) => updateConnect('message', event.target.value)} required />
            </label>
            <button className="btn primary" type="submit">Send Request <ArrowIcon /></button>
          </form>
        </div>
      </div> : null}
    </header>
  );
}

function Hero({ navigate }) {
  return (
    <section className="hero">
      <div className="container hero-shell">
        <div className="hero-image-panel reveal">
          <SiteImage src="/assets/hero-consultant.jpg" alt="Truck permit consultant helping carriers with registration and compliance" fetchPriority="high" loading="eager" sizes="(max-width: 768px) 100vw, 50vw" />
        </div>
        <div className="hero-content-panel reveal delay-1">
          <div className="hero-copy">
            <span className="eyebrow">Best Truck Permits</span>
            <h1>Truck Permits, Authority Filings, and Compliance Support.</h1>
            <p>Get help with USDOT and MC registration, BOC-3, IFTA, IRP, UCR, state permits, renewals, and dispatch-ready paperwork.</p>
            <button className="btn primary round-btn" type="button" onClick={() => navigate('/contact')}>Get Started <ArrowIcon /></button>
          </div>
          <div className="hero-meta">
            <article>
              <strong>50 States</strong>
              <i></i>
              <p>Nationwide support for DOT, MC, BOC-3, IFTA, IRP, UCR, and state filings.</p>
            </article>
          </div>
          <div className="hero-card">
            <div className="mini-slider" aria-label="Truck permit service highlights">
              {sliderCards.map((card) => (
                <article className="mini-slide" key={card.title}>
                  <SiteImage src={card.image} alt={card.title} loading="lazy" sizes="(max-width: 768px) 70vw, 244px" />
                  <strong>{card.title}</strong>
                  <p>{card.text}</p>
                </article>
              ))}
            </div>
            <div className="slider-dots" aria-hidden="true">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustBar() {
  const items = ['USDOT', 'MC Authority', 'BOC-3', 'IFTA', 'IRP Plates', 'UCR', 'KYU', 'NY HUT', 'NM WDT'];
  return (
    <section className="trust-band" aria-label="Popular permit services">
      <div className="container trust-list">
        <span className="trust-label">Popular filings</span>
        {items.map((item) => <span key={item}>{item}</span>)}
      </div>
    </section>
  );
}

function SectionHeader({ kicker, title, text }) {
  return (
    <div className="section-header">
      <span className="eyebrow">{kicker}</span>
      <h2>{title}</h2>
      {text ? <p>{text}</p> : null}
    </div>
  );
}

function ServicesPreview({ navigate }) {
  return (
    <section className="section solutions-section">
      <div className="container">
        <div className="split-heading">
          <SectionHeader
            kicker="Our solutions"
            title="Truck permit services matched to your operation."
            text="Start a company, activate authority, register interstate vehicles, prepare state permits, or organize dispatch paperwork with one clear filing plan."
          />
          <button className="btn dark" type="button" onClick={() => navigate('/services')}>More Services</button>
        </div>
        <div className="service-grid">
          {services.map((service, index) => (
            <article className="service-card reveal" key={service.slug}>
              <div>
                <span className="service-index">{String(index + 1).padStart(2, '0')}.</span>
                <h3>{service.title}</h3>
              </div>
              <p>{service.summary}</p>
              <button type="button" onClick={() => navigate(`/services/${service.slug}`)}>Read More <ArrowIcon /></button>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Process() {
  const steps = [
    ['Permit discovery', 'We identify whether you need DOT, MC, BOC-3, IFTA, IRP, UCR, state permits, trip permits, dispatch paperwork, or new authority registration.'],
    ['Document review', 'We review your carrier information, vehicle details, insurance status, address proof, tax items, and supporting documents before filing.'],
    ['Filing and follow-up', 'We prepare the filing path, track status, and guide the next compliance step so your trucking company can keep moving.']
  ];

  return (
    <section className="section process-section">
      <div className="container process-grid">
        <div className="process-image reveal">
          <SiteImage src="/assets/slider-registration.jpg" alt="Truck permit consultant reviewing registration paperwork" loading="lazy" sizes="(max-width: 768px) 100vw, 50vw" />
          <div className="growth-badge">
            <strong>Document-first review</strong>
            <span>Identify missing information before filing</span>
          </div>
        </div>
        <div className="steps process-copy reveal delay-1">
          <SectionHeader
            kicker="Our process"
            title="A clear filing process from document review to follow-up."
            text="Tell us how and where you operate. We identify the likely filings, check document readiness, and outline the next step."
          />
          {steps.map(([title, text], index) => (
            <article className="step" key={title}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyChoose() {
  const points = ['Fast and reliable permit guidance', 'Affordable pricing with clear next steps', 'Compliance-focused support team', 'Federal, state, and dispatch services in one place'];
  return (
    <section className="section why-section">
      <div className="container why-grid">
        <div>
          <span className="eyebrow">Why carriers call us</span>
          <h2>Know what to file before the truck is waiting.</h2>
          <p>We connect federal registration, vehicle credentials, state permits, renewals, and operating paperwork in one practical plan.</p>
        </div>
        <div className="why-list">
          {points.map((point) => (
            <div key={point}>
              <span className="check-icon" aria-hidden="true"></span>
              <p>{point}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CompanyStory({ navigate }) {
  return (
    <section className="section company-section">
      <div className="container company-grid">
        <div className="company-copy">
          <span className="eyebrow">Our company</span>
          <h2>One organized place for authority, permits, and renewals.</h2>
          <p>
            Best Truck Permits helps owner-operators and fleets handle the paperwork that keeps trucks legal on the road: authority filings, renewals, permit setup, state registrations, and dispatch-ready documentation.
          </p>
          <div className="company-metrics">
            <div>
              <strong>50</strong>
              <span>State support</span>
            </div>
            <div>
              <strong>6+</strong>
              <span>Core services</span>
            </div>
          </div>
          <button className="btn dark" type="button" onClick={() => navigate('/about')}>About Our Team</button>
        </div>
        <div className="company-image">
          <SiteImage src="/assets/permit-compliance-desk.jpg" alt="Truck permit compliance documents and logistics desk" loading="lazy" sizes="(max-width: 768px) 100vw, 50vw" />
          <div className="customer-note">
            <strong>One plan</strong>
            <span>for connected carrier filings</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProjectShowcase({ navigate }) {
  const projects = [
    {
      title: 'Authority Setup',
      text: 'DOT, MC, BOC-3 and new carrier paperwork',
      image: '/assets/slider-registration.jpg',
      alt: 'DOT MC and BOC-3 authority setup for new trucking company registration'
    },
    {
      title: 'Permit Management',
      text: 'IRP, IFTA, UCR and state permit filings',
      image: '/assets/permit-compliance-desk.jpg',
      alt: 'Truck permit management desk for IFTA IRP UCR and state filings'
    },
    {
      title: 'Dispatch Ready',
      text: 'Carrier packets, paperwork and load support',
      image: '/assets/hero-truck-permits.jpg',
      alt: 'Dispatch ready carrier paperwork and trucking compliance support'
    }
  ];

  return (
    <section className="section project-section">
      <div className="container">
        <div className="split-heading">
          <SectionHeader
            kicker="How we help"
            title="From new authority to road-ready paperwork."
            text="Practical support for federal registration, recurring permits, state filings, and carrier documents."
          />
          <button className="btn primary" type="button" onClick={() => navigate('/contact')}>Connect</button>
        </div>
        <div className="project-grid">
          {projects.map((project, index) => (
            <article className="project-card" key={project.title}>
              <SiteImage className="project-card-image" src={project.image} alt={project.alt} loading="lazy" sizes="(max-width: 768px) 100vw, 33vw" />
              <span>{String(index + 1).padStart(2, '0')}.</span>
              <div>
                <h3>{project.title}</h3>
                <p>{project.text}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function BlogPreview({ posts, navigate }) {
  return (
    <section className="section blog-preview">
      <div className="container">
        <SectionHeader
          kicker="Latest insights"
          title="Straight answers to common truck permit questions."
          text="Understand what a filing does, who may need it, what to prepare, and what usually happens next."
        />
        <div className="blog-grid">
          {posts.slice(0, 3).map((post) => <BlogCard key={post.id} post={post} navigate={navigate} />)}
        </div>
      </div>
    </section>
  );
}

function BlogCard({ post, navigate }) {
  const image = post.imageUrl || post.image_url || '/assets/permit-compliance-desk.jpg';
  return (
    <article className="blog-card">
      <SiteImage src={image} alt={`${post.title} Best Truck Permits blog image`} loading="lazy" onError={(event) => { event.currentTarget.src = '/assets/permit-compliance-desk.jpg'; }} />
      <span>{post.category}</span>
      <h3>{post.title}</h3>
      <p>{post.excerpt}</p>
      <button className="btn primary blog-read-more" type="button" onClick={() => navigate(`/blog/${post.id}`)}>Read More <ArrowIcon /></button>
    </article>
  );
}

function Faq({ navigate, limit }) {
  const visibleFaqs = limit ? faqs.slice(0, limit) : faqs;
  return (
    <section className="section faq-section">
      <div className="container faq-grid">
        <SectionHeader kicker="FAQ" title="Questions carriers ask before filing." />
        <div className="faq-list">
          {visibleFaqs.map((faq) => (
            <details key={faq.question}>
              <summary>{faq.question}</summary>
              <p>{faq.answer}</p>
            </details>
          ))}
          {limit && faqs.length > limit ? (
            <button className="btn primary faq-more" type="button" onClick={() => navigate('/faq')}>Read More FAQs <ArrowIcon /></button>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function CustomerReviews() {
  return (
    <section className="section reviews-section">
      <div className="container">
        <div className="split-heading">
          <SectionHeader
            kicker="Carrier support scenarios"
            title="Support for the moments that can hold up a carrier."
            text="Choose the situation that matches your operation, from new authority and route expansion to renewals, dispatch paperwork, and factoring preparation."
          />
        </div>
        <div className="review-grid">
          {supportScenarios.map((scenario) => (
            <article className="review-card" key={scenario.title}>
              <div className="review-person">
                <SiteImage src={scenario.image} alt={`${scenario.title} trucking permit support`} loading="lazy" />
                <div>
                  <strong>{scenario.title}</strong>
                  <span>{scenario.label}</span>
                </div>
              </div>
              <p>{scenario.text}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Cta({ navigate }) {
  return (
    <section className="cta-band">
      <div className="container cta-grid">
        <div>
          <span className="eyebrow">Request a call</span>
          <h2>Ready to get your truck permit handled?</h2>
        </div>
        <div className="cta-actions">
          <button className="btn primary light" type="button" onClick={() => navigate('/contact')}>Request Quote</button>
          <a className="btn ghost light" href={`mailto:${email}`}>{email}</a>
        </div>
      </div>
    </section>
  );
}

function Home({ navigate, posts }) {
  return (
    <main>
      <Hero navigate={navigate} />
      <TrustBar />
      <WhyChoose />
      <CompanyStory navigate={navigate} />
      <ServicesPreview navigate={navigate} />
      <ProjectShowcase navigate={navigate} />
      <Process />
      <CustomerReviews />
      <BlogPreview posts={posts} navigate={navigate} />
      <Faq navigate={navigate} limit={5} />
      <Cta navigate={navigate} />
    </main>
  );
}

function ServicesPage({ navigate }) {
  return (
    <main>
      <PageHero kicker="Services" title="Truck permit services for new and active carriers." text="Get help with federal authority, interstate credentials, state permits, renewals, and the paperwork needed to keep operating." />
      <section className="seo-intro">
        <div className="container seo-intro-grid">
          <div>
            <span className="eyebrow">Nationwide permit support</span>
            <h2>Truck permit services built for owner-operators, fleets, and new trucking companies.</h2>
          </div>
          <p>
            Start with your business type, vehicle details, base state, operating states, and authority status. We will help identify the filings that may apply and the documents to prepare before an application or renewal moves forward.
          </p>
        </div>
      </section>
      <section className="section">
        <div className="container service-listing-grid">
          {services.map((service, index) => (
            <article className="service-detail reveal" key={service.slug} id={service.slug}>
              <div>
                <b>{String(index + 1).padStart(2, '0')}.</b>
                <span className="eyebrow">{service.label}</span>
                <h2>{service.title}</h2>
                <p>{service.summary}</p>
                <button className="btn dark" type="button" onClick={() => navigate(`/services/${service.slug}`)}>Open Service <ArrowIcon /></button>
              </div>
              <ul>
                {service.details.map((detail) => <li key={detail}>{detail}</li>)}
              </ul>
            </article>
          ))}
        </div>
      </section>
      <Cta navigate={navigate} />
    </main>
  );
}

function ServiceDetailPage({ service, navigate }) {
  if (!service) {
    return (
      <main>
        <PageHero kicker="Service" title="Service not found." text="The service page you requested is not available." />
        <section className="section">
          <div className="container">
            <button className="btn dark" type="button" onClick={() => navigate('/services')}>Back to Services</button>
          </div>
        </section>
      </main>
    );
  }

  const related = services.filter((item) => item.slug !== service.slug).slice(0, 3);

  return (
    <main>
      <PageHero kicker={service.label} title={service.title} text={service.hero} />
      <section className="section service-single-section">
        <div className="container service-single-grid">
          <article className="service-single-copy">
            <span className="eyebrow">Dedicated service</span>
            <h2>{service.title} for carriers across the USA.</h2>
            <p>{service.summary}</p>
            <p>{service.overview}</p>
            <p>
              Best Truck Permits helps owner-operators and fleets prepare the right information, avoid common filing delays, and keep the permit process moving with clear communication.
            </p>
            <ul>
              {service.details.map((detail) => <li key={detail}>{detail}</li>)}
            </ul>
            <button className="btn primary" type="button" onClick={() => navigate('/contact')}>Request This Service <ArrowIcon /></button>
          </article>
          <aside className="service-sidebar">
            <SiteImage src="/assets/permit-compliance-desk.jpg" alt={`${service.title} documents`} />
            <h3>Other services</h3>
            {related.map((item) => (
              <button type="button" key={item.slug} onClick={() => navigate(`/services/${item.slug}`)}>{item.title}</button>
            ))}
          </aside>
        </div>
      </section>
      <section className="seo-band">
        <div className="container seo-card-grid">
          <article>
            <h3>What this service covers</h3>
            <p>
              We review the operation, identify the likely filing path, provide a document checklist, and explain related registrations or renewals that may affect timing.
            </p>
          </article>
          <article>
            <h3>Who this service helps</h3>
            <p>
              Owner-operators, dispatch-ready startups, small fleets, and growing carriers can use this service to understand the filing sequence, required details, and common paperwork gaps before a permit or registration request is submitted.
            </p>
          </article>
          <article>
            <h3>Why filing order matters</h3>
            <p>
              DOT, MC, BOC-3, UCR, IFTA, IRP, state permits, insurance, and carrier packet items often depend on one another. A cleaner filing order helps reduce delays, rejected information, and repeated follow-up.
            </p>
          </article>
        </div>
      </section>
      <section className="section">
        <div className="container seo-copy-card">
          <span className="eyebrow">Plan before filing</span>
          <h2>Prepare for {service.title.toLowerCase()} with fewer surprises.</h2>
          <p>
            Bring your legal business details, USDOT or MC number if assigned, vehicle information, base state, planned routes, insurance status, and any notices you have received. Those details help determine which form, account, or credential comes next.
          </p>
          <p>
            Depending on the operation, related steps can include BOC-3, insurance filings, UCR, IFTA, IRP, state mileage or highway-use accounts, trip permits, carrier packets, or renewal updates. We explain those dependencies before work begins.
          </p>
          <button className="btn primary" type="button" onClick={() => navigate('/contact')}>Get Filing Help <ArrowIcon /></button>
        </div>
      </section>
      <Cta navigate={navigate} />
    </main>
  );
}

function PageHero({ kicker, title, text }) {
  return (
    <section className="page-hero">
      <SiteImage src="/assets/hero-truck-permits.jpg" alt={`${title} truck permit service header`} fetchPriority="high" loading="eager" sizes="100vw" />
      <div className="page-hero-overlay"></div>
      <div className="container page-hero-content">
        <h1>{title}</h1>
        <div className="breadcrumb-pill" aria-label="Breadcrumb">
          <span className="home-icon" aria-hidden="true"></span>
          <a href="/">Home</a>
          <i className="crumb-separator" aria-hidden="true"></i>
          <strong>{kicker}</strong>
        </div>
        {text ? <p>{text}</p> : null}
      </div>
    </section>
  );
}

const policyPages = {
  terms: {
    path: '/terms-of-service',
    kicker: 'Terms of Service',
    title: 'Terms of Service for paid truck permit and compliance support.',
    text: 'These terms explain how LoadaiX operates Best Truck Permits and provides paid trucking permit services, filing support, registration assistance, and customer communication.',
    updated: 'June 12, 2026',
    introTitle: 'Terms for using Best Truck Permits services',
    intro:
      'Best Truck Permits is a service brand operated by LoadaiX. In these Terms, "Best Truck Permits," "we," "us," and "our" refer to LoadaiX operating the Best Truck Permits website and services. We provide paid support for trucking company registration, USDOT and MC authority, BOC-3 filing, IFTA, IRP plates, UCR, state truck permits, dispatch paperwork, factoring setup preparation, and related carrier compliance services. By using this website, requesting a quote, submitting a form, purchasing a package, or communicating with our team, you agree to the terms below.',
    sections: [
      {
        title: '1. Legal entity and service brand',
        body:
          'LoadaiX operates Best Truck Permits as a trucking permit, authority registration, compliance support, and administrative filing assistance service. Customer contracts, website use, paid service orders, package purchases, refund requests, and support communication are with LoadaiX operating under the Best Truck Permits brand unless a separate written agreement states otherwise.'
      },
      {
        title: '2. Services we provide',
        body:
          'We help carriers, owner-operators, trucking startups, and fleets prepare, organize, review, and submit information for truck permit and registration services. Our services may include administrative support, document review, application preparation, status follow-up, renewal reminders, carrier packet preparation, and guidance for agency-related requirements. We are not a government agency and we do not issue permits directly unless an applicable agency approves or processes the requested filing.'
      },
      {
        title: '3. Customer responsibilities',
        body:
          'Customers are responsible for providing accurate, complete, current, and truthful information before any filing or paid service begins. This may include legal business name, mailing address, phone number, email, EIN or tax information, USDOT or MC details, vehicle information, insurance details, operating states, mileage records, and supporting documents. Delays, rejections, corrections, penalties, or additional fees caused by missing or inaccurate information are the customer responsibility.'
      },
      {
        title: '4. Fees, payment, Paddle, and agency costs',
        body:
          'LoadaiX, through Best Truck Permits, charges service fees for the time, review, preparation, administrative work, consultation, follow-up, and support provided by our team. Online card payments may be processed by Paddle, which may act as the authorized reseller and merchant of record for eligible online transactions. Some services may also require government, state, third-party, processing, or agency fees. Unless stated in writing, agency fees and third-party charges are separate from our service fees. Prices may vary based on filing type, number of trucks, states involved, urgency, renewal status, and document complexity.'
      },
      {
        title: '5. No guarantee of approval, timing, or agency outcome',
        body:
          'We work to prepare and support filings carefully, but we cannot guarantee that any government agency, factoring company, broker, insurer, or third party will approve, activate, renew, or process an application by a specific date. Processing timelines may change because of agency workload, customer document issues, payment holds, compliance flags, insurance problems, system outages, or requests for additional information.'
      },
      {
        title: '6. Website information and educational content',
        body:
          'The website includes educational information about truck permits, DOT and MC registration, BOC-3, IFTA, IRP, UCR, state permits, factoring, and dispatch paperwork. This information is provided for general business understanding and does not replace legal, tax, insurance, accounting, or government agency advice. Customers should confirm final requirements with the applicable agency or qualified professional when needed.'
      },
      {
        title: '7. Communication consent',
        body:
          'When you submit a form, call us, email us, use WhatsApp, request a quote, or purchase a service, you authorize LoadaiX and Best Truck Permits to contact you by phone, text, email, WhatsApp, or other reasonable business communication methods about your request, service status, missing documents, payment, renewal reminders, and related trucking permit services.'
      },
      {
        title: '8. Limitation of liability',
        body:
          'To the fullest extent permitted by law, LoadaiX and Best Truck Permits are not responsible for indirect, incidental, lost-profit, downtime, load cancellation, agency penalty, enforcement, or business interruption damages arising from delayed, rejected, incomplete, inaccurate, or unavailable filings. Our responsibility is limited to the amount paid directly to Best Truck Permits for the specific service involved.'
      },
      {
        title: '9. Changes to these terms',
        body:
          'LoadaiX may update these Terms of Service as our services, pricing, technology, or business practices change. The updated date on this page shows the latest version. Continued use of our website or services after an update means you accept the revised terms.'
      }
    ]
  },
  privacy: {
    path: '/privacy-policy',
    kicker: 'Privacy Policy',
    title: 'Privacy Policy for truck permit customers and website visitors.',
    text: 'This policy explains what information Best Truck Permits collects and how we use it to provide trucking permit, registration, filing, and support services.',
    updated: 'June 11, 2026',
    introTitle: 'How Best Truck Permits handles customer information',
    intro:
      'Best Truck Permits collects information needed to respond to quote requests, prepare paid truck permit services, support trucking company registration, manage customer communication, and improve our website. We aim to keep customer information limited to what is needed for DOT and MC registration support, BOC-3, IFTA, IRP, UCR, state permits, factoring preparation, dispatch paperwork, and related services.',
    sections: [
      {
        title: '1. Information we may collect',
        body:
          'We may collect your name, company name, phone number, email address, service requested, message details, trucking business information, authority details, vehicle information, operating states, documents you provide, payment-related records, and communication history. If you use the admin, blog, quote, contact, newsletter, or WhatsApp features, the information you submit may be stored so our team can respond and provide service.'
      },
      {
        title: '2. How we use information',
        body:
          'We use customer information to prepare quotes, contact customers, process paid services, organize filing documents, submit or support permit applications, provide status updates, manage renewals, improve service quality, maintain internal records, publish customer-requested blog or service content, and protect the website from misuse. We may also use aggregated, non-personal information to understand which truck permit services customers request most often.'
      },
      {
        title: '3. Sharing with agencies and service providers',
        body:
          'When necessary to perform a requested service, we may share relevant information with government agencies, state filing portals, process agent providers, payment processors, technical vendors, factoring-related contacts, or other service providers involved in the requested trucking service. We do not sell customer personal information as a business model.'
      },
      {
        title: '4. Cookies, analytics, and website data',
        body:
          'Our website may use basic cookies, browser storage, analytics tools, security tools, and server logs to keep the website working, remember site interactions, measure performance, improve content quality, and understand visitor behavior. Browser and server data may include IP address, device type, pages visited, referring website, approximate location, and time of visit.'
      },
      {
        title: '5. Data security',
        body:
          'We use reasonable administrative, technical, and organizational safeguards designed to protect customer information. No website, email, database, hosting account, or internet transmission is completely secure. Customers should avoid sending highly sensitive information until requested through an appropriate service channel.'
      },
      {
        title: '6. Data retention',
        body:
          'We may keep customer information for as long as needed to provide services, maintain business records, support renewal reminders, resolve disputes, comply with legal obligations, prevent fraud, and improve customer support. When information is no longer needed, we may delete, archive, or anonymize it according to business needs.'
      },
      {
        title: '7. Customer choices',
        body:
          'You may contact us to request access, correction, or deletion of information you provided, subject to business, legal, filing, and recordkeeping needs. You may also ask us to stop marketing messages, but we may still contact you about active services, payments, filings, or required service updates.'
      },
      {
        title: '8. Contact about privacy',
        body:
          'For privacy questions, customer information requests, or corrections to your truck permit service details, contact Best Truck Permits by phone or email using the contact information on this website.'
      }
    ]
  },
  refund: {
    path: '/refund-policy',
    kicker: 'Refund Policy',
    title: 'Refund Policy for Best Truck Permits paid service fees and Paddle payments.',
    text: 'This policy explains how LoadaiX handles refund requests for Best Truck Permits services, including online payments processed by Paddle.',
    updated: 'June 12, 2026',
    introTitle: 'Refund terms for Paddle payments, permit packages, and filing support',
    intro:
      'Best Truck Permits is operated by LoadaiX and charges for service work, administrative preparation, document review, filing support, consultation, communication, follow-up, and package-based trucking compliance assistance. Online card payments may be processed by Paddle. This Refund Policy is intended to work together with Paddle refund requirements and all applicable consumer protection laws. Nothing in this policy limits any non-waivable legal right you may have.',
    sections: [
      {
        title: '1. Refund requests and Paddle payments',
        body:
          'If your order was processed by Paddle, Paddle may appear on your card or bank statement and may help process eligible refund requests as the authorized reseller and merchant of record for the transaction. You may contact Best Truck Permits directly, use the receipt or manage-subscription link from your Paddle transaction email, or contact Paddle buyer support with your order email, transaction details, and reason for the request. Approved refunds are normally returned to the original payment method where possible.'
      },
      {
        title: '2. Statutory rights and cooling-off periods',
        body:
          'Customers in some locations may have statutory cancellation, refund, or withdrawal rights, including cooling-off rights for certain online purchases. Where those rights apply, LoadaiX and Best Truck Permits will honor them in accordance with applicable law and Paddle requirements. Some regions provide unconditional withdrawal periods, including 14 days in certain European, UK, Swiss, Turkish, and Israeli consumer transactions, 7 days in certain South Korea, Brazil, China, and Canada consumer transactions, and 5 days in certain Singapore consumer transactions. Service work may begin after purchase when you ask us to start the requested filing support, document review, or administrative preparation.'
      },
      {
        title: '3. Refund requests before work starts',
        body:
          'If you paid for a service and request cancellation before our team begins work, we will review the order and may approve a full refund or appropriate adjustment. Work may include customer intake, document review, checklist creation, filing preparation, agency research, portal setup, support communication, or payment of third-party costs. Discretionary refund requests should be made within 14 days of the transaction date and do not guarantee approval. Requests should be made as soon as possible with your name, company name, service purchased, payment date, order email, and reason for cancellation.'
      },
      {
        title: '4. Service work that has already started',
        body:
          'Because truck permit services are often time-sensitive and labor-based, refund eligibility may be reduced after work begins. If our team has already reviewed documents, prepared applications, created a filing path, contacted you for required information, submitted information, followed up with an agency, or completed administrative support, we may offer a partial refund, service credit, correction, or no refund depending on the work completed, except where a refund is required by law or Paddle policy.'
      },
      {
        title: '5. Agency and third-party fees',
        body:
          'Government, state, agency, portal, process agent, tax, permit, payment processing, or other third-party fees are controlled by outside organizations and may not be refundable once paid, reserved, submitted, or incurred. If an agency rejects, delays, requests corrections, or denies a filing, that does not automatically make our completed service work refundable.'
      },
      {
        title: '6. Duplicate payments, billing errors, and failed fulfillment',
        body:
          'Verified duplicate payments, incorrect charges, or confirmed billing errors will be corrected or refunded after review. If LoadaiX or Best Truck Permits cannot provide the purchased service and no reasonable substitute, correction, or service credit is available, we will review the order for an appropriate refund consistent with Paddle requirements and applicable law.'
      },
      {
        title: '7. Package, renewal, and subscription-style services',
        body:
          'Monthly, annual, renewal, fleet, or package-based services may include several tasks over time. Refund eligibility depends on which tasks have already been completed, started, scheduled, submitted, or reserved. If a package includes future work that has not started, we may review the unused portion for a partial refund, credit, or service adjustment.'
      },
      {
        title: '8. How to request a refund review',
        body:
          'To request a refund review, contact Best Truck Permits through the contact page, phone, or email, or contact Paddle buyer support if your payment was processed by Paddle. Include your full name, business name, order email, service or package purchased, date of payment, amount paid, transaction ID if available, and a clear explanation of the issue. We will review the request based on service status, Paddle requirements, and applicable law.'
      },
      {
        title: '9. Refund timing and method',
        body:
          'When a refund is approved, it is generally issued to the original payment method where possible. Eligible Paddle refunds are processed within 14 days of approval of the request, although bank, card network, Paddle, and payment provider processing times may affect when funds appear in your account. If additional information is needed to complete the review, the refund timeline may depend on when the required details are received.'
      },
      {
        title: '10. Policy updates',
        body:
          'LoadaiX may update this Refund Policy as services, packages, pricing, payment providers, Paddle requirements, or applicable laws change. The updated date on this page reflects the latest version.'
      }
    ]
  },
  disclaimer: {
    path: '/disclaimer',
    kicker: 'Service Disclaimer',
    title: 'Service disclaimer for trucking permit information and paid filing support.',
    text: 'Important information about agency requirements, third-party decisions, service timelines, and educational trucking content.',
    updated: 'July 12, 2026',
    introTitle: 'What our trucking permit information can and cannot do',
    intro:
      'Best Truck Permits provides paid administrative support, document preparation, filing guidance, and educational content for carriers. We are not a government agency, law firm, accounting firm, insurance agency, or tax adviser. Website information is intended to help trucking companies understand common permit topics before requesting service.',
    sections: [
      {
        title: 'Agency decisions and filing outcomes',
        body:
          'Government agencies, state portals, process agents, insurers, factoring companies, brokers, and other third parties control their own approvals, timelines, fees, eligibility rules, and decisions. Best Truck Permits cannot guarantee approval, activation, renewal, acceptance, load booking, factoring approval, or a specific processing date.'
      },
      {
        title: 'Educational trucking content',
        body:
          'Content about USDOT, MC authority, BOC-3, IFTA, IRP, UCR, state permits, dispatch paperwork, factoring preparation, and trucking company registration is provided for general business education. Requirements may change and may vary by state, vehicle, operation type, weight, cargo, and route.'
      },
      {
        title: 'Customer information responsibility',
        body:
          'Customers are responsible for providing accurate, current, complete, and lawful information. Inaccurate business names, EIN details, vehicle records, mileage information, insurance data, addresses, or authority details can cause delays, extra fees, rejected filings, or compliance issues.'
      },
      {
        title: 'Professional advice',
        body:
          'For legal, tax, accounting, insurance, safety compliance, or regulatory enforcement questions, customers should consult the applicable agency or a qualified professional. Our service support does not replace professional advice where that advice is required.'
      }
    ]
  },
  editorial: {
    path: '/editorial-policy',
    kicker: 'Editorial Policy',
    title: 'Editorial Policy for Best Truck Permits educational content.',
    text: 'How we create, review, and update truck permit content for carriers, owner-operators, and fleets.',
    updated: 'July 12, 2026',
    introTitle: 'How our truck permit content is created',
    intro:
      'Best Truck Permits publishes service pages, guides, FAQs, and policy content to help carriers understand common filing topics before requesting paid assistance. Our goal is clear, useful, non-misleading information for trucking company registration, permit planning, and compliance preparation.',
    sections: [
      {
        title: 'Content purpose',
        body:
          'Our content is written to answer practical carrier questions about USDOT and MC registration, BOC-3 filing, IFTA, IRP plates, UCR, state permits, factoring setup, dispatch paperwork, carrier packet readiness, and permit renewal planning.'
      },
      {
        title: 'Review approach',
        body:
          'We review pages for clarity, accuracy, service relevance, internal linking, readability, and whether the page helps a carrier understand next steps. We avoid unsupported guarantees, fake reviews, hidden fees, copied filler copy, and claims that cannot be substantiated.'
      },
      {
        title: 'Updates',
        body:
          'Permit requirements, agency timelines, pricing, and third-party rules may change. We may update pages when services change, when agencies adjust processes, when customers ask recurring questions, or when content needs better explanation.'
      },
      {
        title: 'Transparency',
        body:
          'Best Truck Permits may charge service fees for administrative work, consultation, filing preparation, and package support. Educational content is not a guarantee of agency approval or a promise that every carrier needs every service listed.'
      }
    ]
  },
  corrections: {
    path: '/corrections-policy',
    kicker: 'Corrections Policy',
    title: 'Corrections Policy for truck permit website information.',
    text: 'How customers and visitors can report possible errors in our permit, pricing, policy, or service content.',
    updated: 'July 12, 2026',
    introTitle: 'How to request a content correction',
    intro:
      'We want Best Truck Permits content to be clear, current, and useful. If you believe a page contains an error about a service, package, policy, phone number, filing topic, state permit, or trucking requirement, contact us so we can review it.',
    sections: [
      {
        title: 'What to include',
        body:
          'Please include the page URL, the text you believe is incorrect, the reason you believe it should be changed, and any source or agency reference that supports the correction request.'
      },
      {
        title: 'Review process',
        body:
          'Our team reviews correction requests for accuracy, relevance, and service impact. If a correction is appropriate, we may update the page, clarify the wording, add a disclaimer, adjust pricing language, or remove outdated content.'
      },
      {
        title: 'Urgent service details',
        body:
          'If the issue affects an active paid service, contact us directly by phone or email so we can review your account, documents, filing status, or payment details promptly.'
      },
      {
        title: 'No guarantee of publication',
        body:
          'Submitting a correction request does not guarantee a specific wording change, but we will consider good-faith requests that help carriers understand Best Truck Permits services more accurately.'
      }
    ]
  },
  cookies: {
    path: '/cookie-policy',
    kicker: 'Cookie Policy',
    title: 'Cookie Policy for Best Truck Permits website visitors.',
    text: 'How cookies, browser storage, analytics, and server logs may support the website and customer service experience.',
    updated: 'July 12, 2026',
    introTitle: 'How cookies and browser data may be used',
    intro:
      'Best Truck Permits may use limited cookies, browser storage, analytics, and server logs to operate the website, remember interactions, protect forms, understand page performance, and improve trucking permit content.',
    sections: [
      {
        title: 'Essential site functions',
        body:
          'Some browser storage or cookies may support navigation, admin login sessions, form handling, captcha validation, checkout configuration, and basic website functionality.'
      },
      {
        title: 'Analytics and performance',
        body:
          'We may use analytics or server logs to understand which pages visitors use, how the website performs, what devices access the site, and which trucking permit topics need clearer explanation.'
      },
      {
        title: 'Third-party services',
        body:
          'Payment, hosting, analytics, security, communication, or embedded tools may set their own cookies or collect technical data according to their own policies. Paddle may process payment-related data for eligible checkout transactions.'
      },
      {
        title: 'Managing cookies',
        body:
          'Most browsers allow visitors to block, delete, or limit cookies. Some features, including forms, admin access, checkout, and saved interactions, may not work correctly if cookies or browser storage are disabled.'
      }
    ]
  }
};

function PolicyPage({ page, navigate }) {
  if (!page) return <Home navigate={navigate} posts={seedPosts} />;

  return (
    <main>
      <PageHero kicker={page.kicker} title={page.title} text={page.text} />
      <section className="seo-page-section policy-page-section">
        <div className="container seo-page-grid">
          <article className="seo-copy-card policy-intro-card">
            <span className="eyebrow">Best Truck Permits policy</span>
            <h2>{page.introTitle}</h2>
            <p>{page.intro}</p>
            <p><strong>Last updated:</strong> {page.updated}</p>
            <p>
              These pages are written for Best Truck Permits customers who request paid trucking services such as USDOT registration, MC authority, BOC-3 filing, IFTA fuel tax support, IRP plates, UCR registration, state truck permits, trucking company setup, dispatch paperwork, factoring preparation, and permit renewal support.
            </p>
            <button className="btn primary" type="button" onClick={() => navigate('/contact')}>Contact Best Truck Permits <ArrowIcon /></button>
          </article>
          <aside className="seo-proof-card">
            <SiteImage src="/assets/permit-compliance-desk.jpg" alt={`${page.kicker} for Best Truck Permits paid trucking permit services`} loading="lazy" />
            <div>
              <strong>Paid services</strong>
              <span>Clear terms for permit packages, filing support, and customer requests</span>
            </div>
            <div>
              <strong>Carrier focused</strong>
              <span>Written for owner-operators, new authorities, and commercial fleets</span>
            </div>
          </aside>
        </div>
      </section>
      <section className="seo-band policy-detail-section">
        <div className="container policy-grid">
          {page.sections.map((section) => (
            <article className="faq-page-card policy-card" key={section.title}>
              <h3>{section.title}</h3>
              <p>{section.body}</p>
            </article>
          ))}
        </div>
      </section>
      <section className="section">
        <div className="container faq-cta policy-cta">
          <h2>Questions about a paid truck permit service?</h2>
          <p>Contact Best Truck Permits before placing an order if you have questions about service fees, agency costs, required documents, timelines, privacy, or refund eligibility.</p>
          <button className="btn primary" type="button" onClick={() => navigate('/contact')}>Ask a Policy Question <ArrowIcon /></button>
        </div>
      </section>
    </main>
  );
}

function AboutPage({ navigate }) {
  return (
    <main>
      <PageHero kicker="About Us" title="A truck permit company built for carriers who need clear answers." text="Best Truck Permits helps owner-operators, startups, and fleets keep authority, registrations, and state filings organized." />
      <section className="seo-page-section">
        <div className="container seo-page-grid">
          <article className="seo-copy-card">
            <span className="eyebrow">Best Truck Permits</span>
            <h2>Nationwide permit support for trucking companies, owner-operators, and fleets.</h2>
            <p>
              Best Truck Permits supports carriers that need help with trucking company registration, USDOT number filing, MC authority, BOC-3, UCR, IFTA, IRP plates, state truck permits, trip permits, and dispatch-ready paperwork. Our goal is to make permit requirements easier to understand before a carrier loses time to missing documents, wrong filing order, or avoidable agency delays.
            </p>
            <p>
              We work with new trucking companies preparing to launch, active carriers adding routes, and fleets that need reliable renewal tracking. Our team explains what a filing does, what documents may be needed, and which related step could affect the timeline.
            </p>
            <button className="btn primary" type="button" onClick={() => navigate('/contact')}>Request Permit Help <ArrowIcon /></button>
          </article>
          <aside className="seo-proof-card">
            <SiteImage src="/assets/permit-compliance-desk.jpg" alt="Best Truck Permits nationwide truck permit filing support" loading="lazy" />
            <div>
              <strong>50 States</strong>
              <span>Federal, interstate, and state filing support</span>
            </div>
            <div>
              <strong>Core Filings</strong>
              <span>DOT, MC, BOC-3, UCR, IFTA, IRP, KYU, NY HUT, NM WDT</span>
            </div>
          </aside>
        </div>
      </section>
      <section className="seo-band">
        <div className="container seo-card-grid">
          {['Authority setup for new carriers', 'State permit guidance for active trucks', 'Renewal tracking for annual filings'].map((item) => (
            <article key={item}>
              <h3>{item}</h3>
              <p>Clear steps, filing preparation, and document guidance for trucking companies that want fewer compliance surprises.</p>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}

function FullAboutPage({ navigate }) {
  const values = [
    ['New authority setup', 'We help carriers understand company setup, EIN readiness, USDOT, MC authority, BOC-3, UCR, insurance coordination, and startup paperwork.'],
    ['Interstate permit support', 'We support IFTA, IRP, apportioned plates, UCR, fuel tax reporting, renewal reminders, and multi-state permit planning for active trucks.'],
    ['State truck permits', 'We help identify state-specific filings such as KYU, New York HUT, New Mexico WDT, Oregon permits, trip permits, and route-related requirements.'],
    ['Factoring preparation', 'We help new trucking companies prepare carrier documents that factoring companies commonly request, including authority, insurance, W-9, and business details.'],
    ['Dispatch-ready paperwork', 'We help carriers organize packets, compliance records, permit confirmations, and registration details so operations can move with fewer delays.'],
    ['Plain-language filing guidance', 'We explain what each filing does, when it may apply, what to prepare, and which agency or program makes the final decision.']
  ];

  return (
    <main>
      <PageHero kicker="About Us" title="About Best Truck Permits" text="A nationwide truck permit company helping carriers with DOT, MC, BOC-3, IFTA, IRP, UCR, state permits, factoring setup, and dispatch-ready paperwork." />
      <section className="about-intro-section">
        <div className="container about-intro-grid">
          <div className="about-image-stack">
            <SiteImage src="/assets/slider-registration.jpg" alt="Best Truck Permits authority setup and DOT MC registration support" loading="lazy" />
            <SiteImage src="/assets/permit-compliance-desk.jpg" alt="Truck permit management for IFTA IRP UCR and state permit filings" loading="lazy" />
          </div>
          <article className="about-intro-copy">
            <span className="eyebrow">Who we are</span>
            <h2>We help trucking companies stay legal, organized, and ready to move freight.</h2>
            <p>
              Best Truck Permits is built for owner-operators, new trucking companies, dispatch-ready carriers, and small fleets that need reliable help with commercial trucking paperwork. Our services cover trucking company registration, USDOT number filing, MC authority, BOC-3, UCR, IFTA setup, IRP plates, state truck permits, trip permits, renewal support, and factoring preparation.
            </p>
            <p>
              Carriers often lose time because permit requirements are scattered across federal agencies, state programs, tax accounts, and renewal calendars. We bring those steps into a clearer workflow so you can understand what applies before you dispatch a truck, expand into a new state, renew annual filings, or prepare documents for a broker, dispatcher, or factoring company.
            </p>
            <div className="about-metrics">
              <div><strong>50</strong><span>State support</span></div>
              <div><strong>6+</strong><span>Core permit services</span></div>
              <div><strong>24/7</strong><span>Online request intake</span></div>
            </div>
            <button className="btn primary" type="button" onClick={() => navigate('/contact')}>Talk to Permit Team <ArrowIcon /></button>
          </article>
        </div>
      </section>
      <section className="about-values-section">
        <div className="container">
          <SectionHeader
            kicker="What we do"
            title="Practical permit support for real carrier operations."
            text="Our services follow the way a carrier actually works: set up the business, activate authority, credential the vehicles, plan routes, and track renewals."
          />
          <div className="about-value-grid">
            {values.map(([title, text]) => (
              <article key={title}>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className="about-process-section">
        <div className="container about-process-grid">
          <div>
            <span className="eyebrow">Why carriers choose us</span>
            <h2>Clear filing guidance before you lose time to missing paperwork.</h2>
            <p>
              Whether you are starting a trucking company in California, applying for truck permits in Texas, preparing NY HUT, renewing IFTA and IRP, or comparing factoring options, Best Truck Permits helps you identify the next step and organize the documents that matter.
            </p>
          </div>
          <div className="about-process-list">
            {[
              'Review the service or route that applies to your carrier operation.',
              'Collect business, vehicle, authority, insurance, and tax details.',
              'Prepare the permit or registration path with clear filing priorities.',
              'Track next steps, renewals, and related compliance items.'
            ].map((item, index) => (
              <article key={item}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <p>{item}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <Cta navigate={navigate} />
    </main>
  );
}

function FaqPage({ navigate }) {
  return (
    <main>
      <PageHero kicker="FAQ" title="Truck permit questions answered before you file." text="Find answers about DOT, MC, BOC-3, IFTA, IRP, UCR, state permits, new authority setup, and factoring." />
      <section className="faq-page-section">
        <div className="container">
          <div className="section-header">
            <span className="eyebrow">Truck permit FAQ</span>
            <h2>Helpful answers for carriers comparing truck permit services.</h2>
            <p>Use these answers to understand common permit requirements. For exact filing steps, send your route, vehicle, and authority details so we can guide you based on your operation.</p>
          </div>
          <div className="faq-page-grid">
            {faqs.map((item) => (
              <article className="faq-page-card" key={item.question}>
                <h3>{item.question}</h3>
                <p>{item.answer}</p>
              </article>
            ))}
          </div>
          <div className="faq-cta">
            <h2>Need a filing reviewed?</h2>
            <p>Ask Best Truck Permits about DOT and MC registration, BOC-3, IFTA, IRP plates, UCR, state permits, or factoring setup for your trucking company.</p>
            <button className="btn primary" type="button" onClick={() => navigate('/contact')}>Ask a Permit Question <ArrowIcon /></button>
          </div>
        </div>
      </section>
    </main>
  );
}

function FactoringPage({ navigate }) {
  const points = ['Carrier packet and authority review', 'Factoring document checklist for new trucking companies', 'Support preparing insurance, W-9, MC, DOT, and bank details', 'Guidance for comparing factoring percentages and payment terms'];
  return (
    <main>
      <PageHero kicker="Factoring" title="Factoring setup support for new trucking companies." text="Prepare your carrier documents and compare factoring options so your trucking company can improve cash flow after booking loads." />
      <section className="seo-page-section">
        <div className="container seo-page-grid">
          <article className="seo-copy-card">
            <span className="eyebrow">Truck factoring setup</span>
            <h2>Helping carriers get ready for factoring companies with stronger paperwork.</h2>
            <p>
              Many new trucking companies need factoring because brokers and shippers may not pay immediately. Best Truck Permits helps carriers prepare the documents factoring companies commonly request, including authority details, insurance proof, W-9, carrier packet information, bank details, and compliance records.
            </p>
            <p>
              We do not promise approval or a specific factoring rate, but we help new carriers present cleaner information, understand factoring percentages, compare terms, and avoid common setup delays. This service pairs well with new authority setup, dispatch preparation, and carrier packet readiness.
            </p>
            <ul>
              {points.map((point) => <li key={point}>{point}</li>)}
            </ul>
            <button className="btn primary" type="button" onClick={() => navigate('/contact')}>Ask About Factoring <ArrowIcon /></button>
          </article>
          <aside className="seo-proof-card">
            <SiteImage src="/assets/slider-registration.jpg" alt="Factoring setup documents for new trucking company carrier packet" loading="lazy" />
            <div>
              <strong>Better prepared</strong>
              <span>Organized documents before applying to factoring companies</span>
            </div>
            <div>
              <strong>Carrier focused</strong>
              <span>Support for owner-operators, dispatch-ready carriers, and small fleets</span>
            </div>
          </aside>
        </div>
      </section>
      <section className="seo-band">
        <div className="container seo-card-grid">
          <article>
            <h3>Factoring for new trucking companies</h3>
            <p>New carriers often need working capital before broker payments arrive. We help organize authority, insurance, W-9, carrier packet, and banking details so factoring conversations start with cleaner documents.</p>
          </article>
          <article>
            <h3>Compare factoring percentages</h3>
            <p>Factoring rates, reserve terms, fuel card options, recourse rules, and contract lengths can vary. We help carriers understand what to ask before choosing a factoring company.</p>
          </article>
          <article>
            <h3>Built for dispatch readiness</h3>
            <p>Factoring setup pairs with DOT, MC, BOC-3, UCR, IFTA, IRP, insurance, broker packets, and dispatch paperwork so new trucking companies can move toward paid loads with fewer document delays.</p>
          </article>
        </div>
      </section>
      <section className="section">
        <div className="container seo-copy-card">
          <span className="eyebrow">Factoring service guide</span>
          <h2>Truck factoring setup support for carriers comparing better percentages and faster payment options.</h2>
          <p>
            Best Truck Permits helps owner-operators and new trucking companies prepare for factoring review by organizing the business, authority, insurance, banking, carrier packet, and invoice documents commonly requested during onboarding.
          </p>
          <p>
            We help carriers collect and review documents, understand common factoring questions, and prepare a stronger packet before speaking with factoring providers. This page is informational and practical: it supports better decision-making without promising a specific approval, rate, or factoring company outcome.
          </p>
          <button className="btn primary" type="button" onClick={() => navigate('/contact')}>Prepare Factoring Packet <ArrowIcon /></button>
        </div>
      </section>
    </main>
  );
}

function StateMarketPage({ state, type, navigate }) {
  const isCompany = type === 'company';
  const title = isCompany ? `Trucking Company in ${state.state}` : `Truck Permits in ${state.state}`;
  const intro = isCompany
    ? `Best Truck Permits helps carriers start and register a trucking company in ${state.state} with guidance for business setup, USDOT, MC authority, BOC-3, UCR, IFTA, IRP, and state permit requirements.`
    : `Best Truck Permits helps carriers get truck permits in ${state.state}, including federal authority support, fuel tax, apportioned registration, UCR, trip permits, and state-specific operating filings.`;
  const items = isCompany
    ? ['New trucking company registration checklist', 'USDOT and MC authority preparation', 'BOC-3, UCR, IFTA, IRP, and state filing support', 'Carrier packet and factoring-readiness guidance']
    : ['State permit review before entering a jurisdiction', 'IFTA, IRP, UCR, and renewal support', 'Temporary trip permit and mileage tax guidance', 'Paperwork organization for dispatch and compliance'];

  return (
    <main>
      <PageHero kicker={state.state} title={title} text={intro} />
      <section className="seo-page-section">
        <div className="container seo-page-grid">
          <article className="seo-copy-card">
            <span className="eyebrow">{isCompany ? 'Company registration' : 'Truck permit support'}</span>
            <h2>{isCompany ? `Start a trucking company in ${state.state} with the right filings in the right order.` : `Get ${state.state} truck permit help for interstate and state-specific compliance.`}</h2>
            <p>{intro}</p>
            <p>
              For {state.state} carriers and out-of-state trucks, important filings may include {state.focus} Best Truck Permits helps owner-operators and fleets understand which paperwork applies before dispatch, renewal, or route expansion.
            </p>
            <ul>
              {items.map((item) => <li key={item}>{item}</li>)}
            </ul>
            <button className="btn primary" type="button" onClick={() => navigate('/contact')}>Request {state.state} Help <ArrowIcon /></button>
          </article>
          <aside className="seo-proof-card">
            <SiteImage src={isCompany ? '/assets/slider-registration.jpg' : '/assets/permit-compliance-desk.jpg'} alt={`${title} DOT MC IFTA IRP permit support`} loading="lazy" />
            <div>
              <strong>{state.state}</strong>
              <span>{isCompany ? 'Authority and registration support' : 'Truck permit and renewal support'}</span>
            </div>
            <div>
              <strong>Nationwide</strong>
              <span>Support for carriers operating across multiple states</span>
            </div>
          </aside>
        </div>
      </section>
      <section className="seo-band">
        <div className="container state-link-grid">
          {stateMarkets.filter((item) => item.slug !== state.slug).slice(0, 4).map((item) => (
            <button type="button" key={item.slug} onClick={() => navigate(`/${isCompany ? 'trucking-company-in' : 'truck-permits-in'}-${item.slug}`)}>
              {isCompany ? `Trucking Company in ${item.state}` : `Truck Permits in ${item.state}`}
            </button>
          ))}
        </div>
      </section>
    </main>
  );
}

function PackagesPage({ navigate }) {
  return (
    <main>
      <PageHero
        kicker="Packages"
        title="Truck permit packages and pricing for growing carriers."
        text="Choose a setup, renewal, or fleet compliance package built for USDOT, MC, BOC-3, IFTA, IRP, UCR, and state permit workflows."
      />
      <section className="pricing-section">
        <div className="container">
          <div className="pricing-heading">
            <div>
              <span className="eyebrow">Simple pricing</span>
              <h2>Our Pricing <span>Plan.</span></h2>
            </div>
            <p>
              These packages group common setup, renewal, tax, state-permit, dispatch, and compliance tasks. Confirm the included work and any separate government or third-party fees before checkout.
            </p>
          </div>
          <div className="pricing-grid">
            {packages.map((item) => (
              <article className={item.featured ? 'pricing-card featured' : 'pricing-card'} key={item.name}>
                {item.featured ? <span className="pricing-badge">Popular</span> : null}
                <div className="pricing-card-head">
                  <h2>{item.name}</h2>
                  <p>{item.summary}</p>
                </div>
                <div className="price-row">
                  {item.oldPrice ? <del>{item.oldPrice}</del> : null}
                  <strong>{item.price}</strong>
                  <span>{item.note}</span>
                </div>
                <ul>
                  {item.features.map((feature) => <li key={feature}>{feature}</li>)}
                </ul>
                <button className={item.featured ? 'btn primary' : 'btn dark'} type="button" onClick={() => openPaddleCheckout(item)}>
                  Choose Plan <ArrowIcon />
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className="seo-intro package-seo">
        <div className="container seo-intro-grid">
          <div>
            <span className="eyebrow">Nationwide trucking compliance packages</span>
            <h2>Permit pricing built for new authorities, active owner-operators, and growing fleets.</h2>
          </div>
          <p>
            Best Truck Permits helps carriers compare practical truck permit packages for company registration, USDOT and MC authority, BOC-3 filing, UCR renewal, IFTA setup, IRP plates, KYU, New York HUT, New Mexico WDT, Oregon permit reporting, 2290 filing, fixed-fee dispatch support, load searching, rate negotiation, paperwork handling, invoicing, and annual compliance support. Choose the package that matches your route, fleet size, filing schedule, and dispatch needs.
          </p>
        </div>
      </section>
      <Cta navigate={navigate} />
    </main>
  );
}

function BlogPage({ posts, navigate }) {
  return (
    <main>
      <PageHero kicker="Resource Center" title="Truck permit and authority guides for carriers." text="Get clear, practical answers about USDOT and MC registration, BOC-3, IFTA, IRP, UCR, state permits, and carrier paperwork." />
      <section className="seo-intro">
        <div className="container seo-intro-grid">
          <div>
            <span className="eyebrow">Trucking compliance blog</span>
            <h2>Start with the filing that matches your next move.</h2>
          </div>
          <p>
            Each guide explains who may need the filing, what records to gather, how it connects to other credentials, and which questions to confirm with the responsible agency.
          </p>
        </div>
      </section>
      <section className="section">
        <div className="container blog-grid">
          {posts.map((post) => <BlogCard key={post.id} post={post} navigate={navigate} />)}
        </div>
      </section>
    </main>
  );
}

function BlogPostPage({ post, navigate }) {
  if (!post) {
    return (
      <main>
        <PageHero kicker="Blog" title="Blog post not found." text="The article you requested is not available." />
        <section className="section">
          <div className="container">
            <button className="btn dark" type="button" onClick={() => navigate('/blog')}>Back to Blog</button>
          </div>
        </section>
      </main>
    );
  }

  const image = post.imageUrl || post.image_url || '/assets/permit-compliance-desk.jpg';
  const articleHtml = /<\/?[a-z][\s\S]*>/i.test(post.content)
    ? post.content
    : `<p>${String(post.content).replaceAll('\n', '</p><p>')}</p>`;

  return (
    <main>
      <PageHero kicker={post.category} title={post.title} text={post.excerpt} />
      <article className="section article-page">
        <div className="container article-grid">
          <div className="article-main">
            <div className="article-image">
              <SiteImage src={image} alt={`${post.title} Best Truck Permits article`} onError={(event) => { event.currentTarget.src = '/assets/permit-compliance-desk.jpg'; }} />
            </div>
            <div className="article-copy">
              <p className="post-date">{new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
              <div className="rich-article" dangerouslySetInnerHTML={{ __html: articleHtml }} />
              <p>
                Need help with this permit or filing? Best Truck Permits can review your details, prepare next steps, and help you avoid avoidable delays.
              </p>
              <button className="btn dark" type="button" onClick={() => navigate('/blog')}>Back to Blog</button>
            </div>
          </div>
          <aside className="article-sidebar">
            <div className="article-sidebar-card article-sidebar-contact">
              <span className="eyebrow">Need permit help?</span>
              <h3>Talk to Best Truck Permits</h3>
              <p>Get help with DOT, MC, BOC-3, IFTA, IRP, UCR, state permits, dispatch paperwork, or factoring preparation.</p>
              <a className="btn primary" href={`tel:${phoneHref}`}>Call Now <ArrowIcon /></a>
            </div>
            <div className="article-sidebar-card">
              <h3>Recent guides</h3>
              {seedPosts.filter((item) => item.id !== post.id).slice(0, 4).map((item) => (
                <button type="button" key={item.id} onClick={() => navigate(`/blog/${item.id}`)}>
                  <span>{item.category}</span>
                  <strong>{item.title}</strong>
                </button>
              ))}
            </div>
            <div className="article-sidebar-card">
              <h3>Core services</h3>
              {services.slice(0, 5).map((service) => (
                <button type="button" key={service.slug} onClick={() => navigate(`/services/${service.slug}`)}>
                  <strong>{service.title}</strong>
                </button>
              ))}
            </div>
          </aside>
        </div>
      </article>
    </main>
  );
}

function ContactPage({ submissions, setSubmissions }) {
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    service: 'DOT and MC Registration',
    message: ''
  });
  const [sent, setSent] = useState(false);

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const submit = async (event) => {
    event.preventDefault();
    const entry = {
      id: crypto.randomUUID(),
      ...form,
      source: 'Contact page',
      createdAt: new Date().toISOString()
    };
    try {
      await apiRequest('submissions.php', {
        method: 'POST',
        body: JSON.stringify(entry)
      });
    } catch {
      // Local fallback keeps preview usable before PHP/MySQL is configured.
    }
    setSubmissions([entry, ...submissions]);
    setForm({ name: '', phone: '', email: '', service: 'DOT and MC Registration', message: '' });
    setSent(true);
  };

  return (
    <main>
      <PageHero kicker="Contact" title="Tell us what your truck needs before it moves." text="Share your authority status, vehicle details, base state, operating states, and deadline so we can review the likely filing path." />
      <section className="contact-info-section">
        <div className="container">
          <div className="contact-section-heading">
            <span className="eyebrow">Talk to our team</span>
            <h2><span>Choose</span> how to reach us.</h2>
          </div>
          <div className="contact-card-grid">
            <article className="contact-info-card">
              <span className="contact-card-icon" aria-hidden="true"><MailIcon /></span>
              <h3>Email us</h3>
              <a href={`mailto:${email}`}>{email}</a>
              <p>For quote requests, permit questions, and paperwork review.</p>
            </article>
            <article className="contact-info-card active">
              <span className="contact-card-icon" aria-hidden="true"><PhoneIcon /></span>
              <h3>Call us</h3>
              <a href={`tel:${phoneHref}`}>{phone}</a>
              <p>Speak with our truck permit support team.</p>
            </article>
            <article className="contact-info-card">
              <span className="contact-card-icon" aria-hidden="true"><ChatIcon /></span>
              <h3>Live support</h3>
              <a href="/contact" onClick={(event) => event.preventDefault()}>Need help?</a>
              <p>Use the form below for DOT, MC, BOC-3, IFTA, IRP, or state permit help.</p>
            </article>
          </div>
        </div>
      </section>
      <section className="section contact-form-section">
        <div className="container contact-grid">
          <form className="contact-form" onSubmit={submit}>
            <span className="eyebrow">Request a permit quote</span>
            <h2>Tell us what trucking permit service you need.</h2>
            {sent ? <p className="success">Thanks. Your request has been saved in admin.</p> : null}
            <label>
              Full name
              <input value={form.name} onChange={(event) => update('name', event.target.value)} required />
            </label>
            <label>
              Phone
              <input value={form.phone} onChange={(event) => update('phone', event.target.value)} required />
            </label>
            <label>
              Email
              <input type="email" value={form.email} onChange={(event) => update('email', event.target.value)} required />
            </label>
            <label>
              Service needed
              <select value={form.service} onChange={(event) => update('service', event.target.value)}>
                {services.map((service) => <option key={service.slug}>{service.title}</option>)}
              </select>
            </label>
            <label>
              Message
              <textarea value={form.message} onChange={(event) => update('message', event.target.value)} rows="5" required />
            </label>
            <button className="btn primary" type="submit">Submit Request <ArrowIcon /></button>
          </form>
          <aside className="contact-image-panel">
            <SiteImage src="/assets/slider-registration.jpg" alt="Best Truck Permits contact support for DOT MC IFTA IRP and state permits" loading="lazy" />
            <div>
              <span className="eyebrow">Nationwide support</span>
              <h3>Fast answers for carriers who need filings done right.</h3>
              <p>We help owner-operators and fleets prepare the right documents before filing.</p>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}

function AdminLoginPage({ navigate, setAdminAuthed }) {
  const adminUsername = 'skbataan@gmail.com';
  const maskedAdminUsername = 's****n@gmail.com';
  const [mode, setMode] = useState('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetMethod, setResetMethod] = useState('current');
  const [recoveryCode, setRecoveryCode] = useState('');
  const createLocalCaptcha = (captchaMode = mode) => {
    const left = Math.floor(Math.random() * 11) + 8;
    const right = Math.floor(Math.random() * 7) + 3;
    const operator = Math.random() > 0.5 ? '+' : '-';
    const expected = operator === '+' ? left + right : left - right;
    return {
      question: `What is ${left} ${operator} ${right}?`,
      answer: '',
      expected: String(expected),
      challenge: { left, right, operator, context: captchaMode === 'reset' ? 'reset' : 'login' }
    };
  };
  const [captcha, setCaptcha] = useState(() => createLocalCaptcha('login'));
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadCaptcha = async (captchaMode = mode) => {
    const fallbackCaptcha = createLocalCaptcha(captchaMode);
    setCaptcha(fallbackCaptcha);
    if (captchaMode === 'reset') {
      return;
    }
    try {
      const data = await apiRequest(`captcha.php?context=${captchaMode === 'reset' ? 'reset' : 'login'}`);
      if (data.question) {
        setCaptcha({ question: data.question, answer: '', expected: '', challenge: null });
      }
    } catch {
      setCaptcha(fallbackCaptcha);
    }
  };

  useEffect(() => {
    loadCaptcha(mode);
  }, [mode]);

  const submit = async (event) => {
    event.preventDefault();
    if (mode === 'reset') {
      if (resetMethod === 'current' && !currentPassword.trim()) {
        setError('Enter the current admin password before setting a new password.');
        return;
      }
      if (resetMethod === 'recovery' && !recoveryCode.trim()) {
        setError('Enter the admin recovery code to reset without the current password.');
        return;
      }
      if (newPassword.trim().length < 10) {
        setError('Create a stronger password with at least 10 characters.');
        return;
      }
      if (!captcha.answer.trim()) {
        setError('Complete the verification question.');
        return;
      }
      if (captcha.expected && captcha.answer.trim() !== captcha.expected) {
        setError('Verification answer is incorrect.');
        setCaptcha(createLocalCaptcha('reset'));
        return;
      }
      try {
        await apiRequest('admin-password.php', {
          method: 'POST',
          body: JSON.stringify({
            username: adminUsername,
            resetMethod,
            currentPassword: currentPassword.trim(),
            recoveryCode: recoveryCode.trim(),
            newPassword: newPassword.trim(),
            captcha: captcha.answer.trim(),
            captchaChallenge: captcha.challenge || null
          })
        });
      } catch (adminError) {
        setError(adminError.message || 'Unable to update the admin password.');
        loadCaptcha('reset');
        return;
      }
      setPassword('');
      setCurrentPassword('');
      setRecoveryCode('');
      setNewPassword('');
      setResetMethod('current');
      setMode('login');
      setError('');
      setNotice('Password updated. You can now log in as admin.');
      loadCaptcha('login');
      return;
    }

    if (!username.trim() || !password.trim() || !captcha.answer.trim()) {
      setError('Enter the admin email, password, and verification answer.');
      return;
    }
    if (captcha.expected && captcha.answer.trim() !== captcha.expected) {
      setNotice('');
      setError('Verification answer is incorrect.');
      setCaptcha(createLocalCaptcha('login'));
      return;
    }

    try {
      await apiRequest('login.php', {
        method: 'POST',
        body: JSON.stringify({
          username: username.trim(),
          password: password.trim(),
          captcha: captcha.answer.trim(),
          captchaChallenge: captcha.challenge || null
        })
      });
      localStorage.setItem('btp-admin-auth', 'true');
      setAdminAuthed(true);
      navigate('/admin');
      return;
    } catch (adminError) {
      setNotice('');
      setError(adminError.message || 'Enter the correct username and password.');
      loadCaptcha('login');
    }
  };

  return (
    <main>
      <PageHero kicker="Admin Login" title="Secure access for Best Truck Permits admin." text="Sign in to review customer requests and publish SEO blog content for Best Truck Permits." />
      <section className="login-section">
        <div className="container login-grid">
          <div className="login-copy">
            <span className="eyebrow">Private dashboard</span>
            <h2>{mode === 'reset' ? 'Reset the admin password securely.' : 'Admin access is available from the direct login URL.'}</h2>
            <p>
              The admin dashboard is hidden from public navigation. The admin account is shown as <strong>{maskedAdminUsername}</strong>. You can update the password with the current password, or use the private recovery code if the password is forgotten.
            </p>
          </div>
          <form className="login-card" onSubmit={submit}>
            {mode === 'reset' ? (
              <p className="admin-mask-note">Resetting password for admin account <strong>{maskedAdminUsername}</strong>.</p>
            ) : (
              <label>
                Admin email
                <input value={username} onChange={(event) => setUsername(event.target.value)} placeholder={maskedAdminUsername} required />
              </label>
            )}
            {mode === 'reset' ? (
              <>
                <div className="reset-method-tabs" role="tablist" aria-label="Password reset method">
                  <button className={resetMethod === 'current' ? 'active' : ''} type="button" onClick={() => { setResetMethod('current'); setError(''); }}>
                    I know current password
                  </button>
                  <button className={resetMethod === 'recovery' ? 'active' : ''} type="button" onClick={() => { setResetMethod('recovery'); setError(''); }}>
                    Forgot password
                  </button>
                </div>
                {resetMethod === 'current' ? (
                  <label>
                    Current password
                    <input type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} placeholder="Enter current admin password" required />
                  </label>
                ) : (
                  <label>
                    Recovery code
                    <input type="password" value={recoveryCode} onChange={(event) => setRecoveryCode(event.target.value)} placeholder="Enter private recovery code" required />
                  </label>
                )}
                <label>
                  New password
                  <input type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} placeholder="Enter new admin password" required />
                </label>
              </>
            ) : (
              <label>
                Password
                <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Enter admin password" required />
              </label>
            )}
            <label>
              Verification
              <span className="captcha-question">{captcha.question}</span>
              <input value={captcha.answer} onChange={(event) => setCaptcha((current) => ({ ...current, answer: event.target.value }))} placeholder="Type the answer" required />
            </label>
            {notice ? <p className="success">{notice}</p> : null}
            {error ? <p className="form-error">{error}</p> : null}
            <button className="btn primary" type="submit">{mode === 'reset' ? (resetMethod === 'recovery' ? 'Reset Password' : 'Update Password') : 'Open Admin'} <ArrowIcon /></button>
            <button className="link-button" type="button" onClick={() => { setMode(mode === 'reset' ? 'login' : 'reset'); setError(''); setNotice(''); setResetMethod('current'); }}>
              {mode === 'reset' ? 'Back to login' : 'Forgot password / reset password'}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}

function AdminPage({ submissions, setSubmissions, posts, setPosts }) {
  const emptyDraft = {
    id: '',
    title: '',
    category: '',
    excerpt: '',
    imageUrl: '',
    date: new Date().toISOString().slice(0, 10),
    content: ''
  };
  const [draft, setDraft] = useState(emptyDraft);
  const [activeTab, setActiveTab] = useState('leads');
  const [adminNotice, setAdminNotice] = useState('');
  const [uploading, setUploading] = useState(false);
  const editorRef = useRef(null);

  const normalizedSubmissions = submissions.map((item) => ({
    ...item,
    createdAt: item.createdAt || item.created_at || new Date().toISOString()
  }));
  const serviceCount = normalizedSubmissions.reduce((map, item) => {
    map[item.service] = (map[item.service] || 0) + 1;
    return map;
  }, {});
  const topService = Object.entries(serviceCount).sort((a, b) => b[1] - a[1])[0]?.[0] || 'No service yet';

  useEffect(() => {
    apiRequest('submissions.php')
      .then((data) => {
        if (Array.isArray(data.submissions)) setSubmissions(data.submissions);
      })
      .catch(() => {});
    apiRequest('posts.php?admin=1')
      .then((data) => {
        if (Array.isArray(data.posts) && data.posts.length) {
          setPosts(mergePosts(data.posts));
        }
      })
      .catch(() => {});
  }, [setPosts, setSubmissions]);

  const syncEditor = () => {
    if (editorRef.current) {
      setDraft((current) => ({ ...current, content: editorRef.current.innerHTML }));
    }
  };

  const runEditorCommand = (command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    syncEditor();
  };

  const editPost = (post) => {
    const nextDraft = {
      id: post.id,
      title: post.title || '',
      category: post.category || '',
      excerpt: post.excerpt || '',
      imageUrl: post.imageUrl || post.image_url || '',
      date: post.date || new Date().toISOString().slice(0, 10),
      content: post.content || ''
    };
    setDraft(nextDraft);
    setActiveTab('editor');
    window.setTimeout(() => {
      if (editorRef.current) editorRef.current.innerHTML = nextDraft.content;
    }, 0);
  };

  const resetDraft = () => {
    setDraft(emptyDraft);
    if (editorRef.current) editorRef.current.innerHTML = '';
  };

  const uploadImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setAdminNotice('');
    try {
      const url = await uploadBlogImage(file);
      setDraft((current) => ({ ...current, imageUrl: url }));
      setAdminNotice('Image uploaded and attached to the blog post.');
    } catch (error) {
      setAdminNotice(error.message || 'Image upload failed. You can paste an image URL instead.');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const addPost = async (event) => {
    event.preventDefault();
    const content = editorRef.current?.innerHTML || draft.content;
    const id = (draft.id || draft.title).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || crypto.randomUUID();
    const post = normalizePost({ ...draft, id, content, imageUrl: draft.imageUrl || '/assets/permit-compliance-desk.jpg' });
    try {
      await apiRequest('posts.php', {
        method: 'POST',
        body: JSON.stringify(post)
      });
      setAdminNotice('Blog post saved to MySQL and published on the website.');
    } catch (error) {
      setAdminNotice(error.message || 'Blog post saved locally. Check PHP/MySQL if it does not appear live.');
    }
    setPosts([post, ...posts.filter((item) => item.id !== id)]);
    resetDraft();
  };

  const clearSubmissions = async () => {
    if (!window.confirm('Clear all form submissions from the dashboard?')) return;
    try {
      await apiRequest('submissions.php', { method: 'DELETE' });
    } catch {}
    setSubmissions([]);
  };

  const deletePost = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await apiRequest(`posts.php?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
    } catch {}
    setPosts(posts.filter((item) => item.id !== id));
  };

  return (
    <main className="admin-dashboard-page">
      <section className="admin-hero">
        <div className="container admin-hero-grid">
          <div>
            <span className="eyebrow">Best Truck Permits admin</span>
            <h1>Lead dashboard and blog publishing center.</h1>
            <p>Review customer requests, track high-intent service leads, and publish SEO-friendly truck permit articles with images and rich HTML content.</p>
          </div>
          <div className="admin-hero-actions">
            <button className="btn primary" type="button" onClick={() => setActiveTab('editor')}>New Blog Post <ArrowIcon /></button>
            <button className="btn dark" type="button" onClick={() => { localStorage.removeItem('btp-admin-auth'); window.location.href = '/admin/login'; }}>Logout <ArrowIcon /></button>
          </div>
        </div>
      </section>

      <section className="admin-metrics container" aria-label="Dashboard metrics">
        <article><span>Total leads</span><strong>{normalizedSubmissions.length}</strong><small>Saved form submissions</small></article>
        <article><span>Published posts</span><strong>{posts.length}</strong><small>Blog and SEO content</small></article>
        <article><span>Top service</span><strong>{topService}</strong><small>Most requested category</small></article>
      </section>

      <section className="admin-workspace container">
        <aside className="admin-sidebar" aria-label="Admin sections">
          <button className={activeTab === 'leads' ? 'active' : ''} type="button" onClick={() => setActiveTab('leads')}>Form submissions</button>
          <button className={activeTab === 'editor' ? 'active' : ''} type="button" onClick={() => setActiveTab('editor')}>Blog editor</button>
          <button className={activeTab === 'posts' ? 'active' : ''} type="button" onClick={() => setActiveTab('posts')}>Published posts</button>
        </aside>

        <div className="admin-content-panel">
          {adminNotice ? <p className="success admin-notice">{adminNotice}</p> : null}

          {activeTab === 'leads' ? (
            <section>
              <div className="panel-heading admin-heading-row">
                <div>
                  <span className="eyebrow">Customer requests</span>
                  <h2>Form submissions</h2>
                </div>
                <button type="button" onClick={clearSubmissions}>Clear all</button>
              </div>
              {normalizedSubmissions.length === 0 ? <p className="muted">No submissions yet.</p> : null}
              <div className="submission-table">
                {normalizedSubmissions.map((item) => (
                  <article className="submission-row" key={item.id}>
                    <div><strong>{item.name}</strong><small>{new Date(item.createdAt).toLocaleString()}</small></div>
                    <span>{item.service}</span>
                    <p>{item.message}</p>
                    <div><a href={`tel:${item.phone}`}>{item.phone}</a><a href={`mailto:${item.email}`}>{item.email}</a></div>
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          {activeTab === 'editor' ? (
            <section>
              <div className="admin-heading-row">
                <div>
                  <span className="eyebrow">Rich text blog system</span>
                  <h2>{draft.id ? 'Edit blog post' : 'Add a new blog post'}</h2>
                </div>
                <button type="button" onClick={resetDraft}>Reset</button>
              </div>
              <form className="admin-blog-editor" onSubmit={addPost}>
                <div className="editor-two-col">
                  <label>Title<input placeholder="Example: Complete DOT and MC Registration Guide for New Carriers" value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} required /></label>
                  <label>Slug<input placeholder="dot-mc-registration-guide" value={draft.id} onChange={(event) => setDraft({ ...draft, id: event.target.value })} /></label>
                </div>
                <div className="editor-two-col">
                  <label>Category<input placeholder="DOT and MC Registration" value={draft.category} onChange={(event) => setDraft({ ...draft, category: event.target.value })} required /></label>
                  <label>Publish date<input type="date" value={draft.date} onChange={(event) => setDraft({ ...draft, date: event.target.value })} required /></label>
                </div>
                <label>SEO excerpt<textarea placeholder="Write a short search-friendly summary for Google and the blog listing." rows="3" value={draft.excerpt} onChange={(event) => setDraft({ ...draft, excerpt: event.target.value })} required /></label>
                <div className="editor-image-row">
                  <label>Featured image URL<input placeholder="/uploads/blog-image.jpg or https://..." value={draft.imageUrl} onChange={(event) => setDraft({ ...draft, imageUrl: event.target.value })} /></label>
                  <label className="upload-field">Upload image<input type="file" accept="image/*" onChange={uploadImage} /></label>
                  {uploading ? <span>Uploading...</span> : null}
                </div>
                {draft.imageUrl ? <SiteImage className="editor-preview-image" src={draft.imageUrl} alt="Blog featured preview" /> : null}
                <div className="rich-toolbar" aria-label="Blog formatting toolbar">
                  <button type="button" onClick={() => runEditorCommand('bold')}>Bold</button>
                  <button type="button" onClick={() => runEditorCommand('italic')}>Italic</button>
                  <button type="button" onClick={() => runEditorCommand('formatBlock', 'H2')}>H2</button>
                  <button type="button" onClick={() => runEditorCommand('insertUnorderedList')}>List</button>
                  <button type="button" onClick={() => { const url = window.prompt('Enter link URL'); if (url) runEditorCommand('createLink', url); }}>Link</button>
                </div>
                <div
                  ref={editorRef}
                  className="rich-editor"
                  contentEditable
                  suppressContentEditableWarning
                  onInput={syncEditor}
                  data-placeholder="Write the full article here. Use headings, lists, links, and helpful permit keywords naturally."
                />
                <button className="btn primary" type="submit">Publish Post <ArrowIcon /></button>
              </form>
            </section>
          ) : null}

          {activeTab === 'posts' ? (
            <section>
              <div className="admin-heading-row">
                <div>
                  <span className="eyebrow">Content library</span>
                  <h2>Published blog posts</h2>
                </div>
              </div>
              <div className="admin-post-list">
                {posts.map((post) => (
                  <article key={post.id}>
                    <SiteImage src={post.imageUrl || post.image_url || '/assets/permit-compliance-desk.jpg'} alt={post.title} />
                    <div>
                      <strong>{post.title}</strong>
                      <small>{post.category} - {post.date}</small>
                    </div>
                    <button type="button" onClick={() => editPost(post)}>Edit</button>
                    <button type="button" onClick={() => deletePost(post.id)}>Delete</button>
                  </article>
                ))}
              </div>
            </section>
          ) : null}
        </div>
      </section>
    </main>
  );
}

function Footer({ navigate }) {
  return (
    <footer className="footer">
      <div className="container footer-grid">
        <div>
          <strong className="footer-brand">Best Truck Permits</strong>
          <p>Fast trucking company registration, permit management, compliance filings, and dispatch support across the United States.</p>
        </div>
        <div>
          <h2>Services</h2>
          {services.slice(0, 5).map((service) => (
            <a key={service.slug} href={`/services/${service.slug}`} onClick={(event) => { event.preventDefault(); navigate(`/services/${service.slug}`); }}>{service.title}</a>
          ))}
        </div>
        <div>
          <h2>Contact</h2>
          <a href={`tel:${phoneHref}`}>{phone}</a>
          <a href={`mailto:${email}`}>{email}</a>
          <span>Fresno, California</span>
        </div>
      </div>
      <div className="container footer-bottom">
        <span>Copyright 2026 Best Truck Permits. All rights reserved.</span>
      </div>
    </footer>
  );
}

function FooterBexon({ navigate }) {
  return (
    <footer className="footer footer-bexon">
      <div className="container footer-main">
        <div className="footer-about">
          <button className="footer-logo" type="button" onClick={() => navigate('/')}>
            <SiteImage src={footerLogo} alt="Best Truck Permits & Dispatch" />
          </button>
          <p>Nationwide truck permit, authority, registration, tax filing, and compliance support for owner-operators and commercial fleets.</p>
          <div className="footer-awards" aria-label="Company highlights">
            <span><strong>50</strong> states</span>
            <span><strong>24/7</strong> request intake</span>
          </div>
        </div>
        <div>
          <h2>Services</h2>
          {services.slice(0, 5).map((service) => (
            <a key={service.slug} href={`/services/${service.slug}`} onClick={(event) => { event.preventDefault(); navigate(`/services/${service.slug}`); }}>{service.title}</a>
          ))}
        </div>
        <div>
          <h2>Resources</h2>
          <a href="/about" onClick={(event) => { event.preventDefault(); navigate('/about'); }}>About Us</a>
          <a href="/packages" onClick={(event) => { event.preventDefault(); navigate('/packages'); }}>Packages</a>
          <a href="/factoring" onClick={(event) => { event.preventDefault(); navigate('/factoring'); }}>Factoring Setup</a>
          <a href="/faq" onClick={(event) => { event.preventDefault(); navigate('/faq'); }}>FAQ</a>
          <a href="/services" onClick={(event) => { event.preventDefault(); navigate('/services'); }}>All Services</a>
          <a href="/blog" onClick={(event) => { event.preventDefault(); navigate('/blog'); }}>Blog</a>
          <a href="/contact" onClick={(event) => { event.preventDefault(); navigate('/contact'); }}>Contact Us</a>
          <a href="/terms-of-service" onClick={(event) => { event.preventDefault(); navigate('/terms-of-service'); }}>Terms of Service</a>
          <a href="/privacy-policy" onClick={(event) => { event.preventDefault(); navigate('/privacy-policy'); }}>Privacy Policy</a>
          <a href="/refund-policy" onClick={(event) => { event.preventDefault(); navigate('/refund-policy'); }}>Refund Policy</a>
        </div>
        <div className="footer-news">
          <h2>Subscribe to Our Newsletter.</h2>
          <form onSubmit={(event) => event.preventDefault()}>
            <input type="email" placeholder="Enter email" aria-label="Email address" />
            <button type="submit" aria-label="Subscribe"><span className="plain-arrow-icon" aria-hidden="true"></span></button>
          </form>
          <label>
            <input type="checkbox" />
            <span>Agree to our <a href="/terms-of-service" onClick={(event) => { event.preventDefault(); navigate('/terms-of-service'); }}><strong>Terms & Condition?</strong></a></span>
          </label>
        </div>
      </div>
      <div className="container footer-bottom">
        <div className="footer-contact-row">
          <a href={`tel:${phoneHref}`}><span className="footer-contact-icon" aria-hidden="true"><PhoneIcon /></span>{phone}</a>
          <a href={`mailto:${email}`}><span className="footer-contact-icon" aria-hidden="true"><MailIcon /></span>{email}</a>
          <span>Fresno, California</span>
        </div>
        <div className="footer-social" aria-label="Social links">
          <span>f</span>
          <span>in</span>
          <span>x</span>
        </div>
        <span>Copyright 2026 Best Truck Permits. All rights reserved.</span>
      </div>
    </footer>
  );
}

function ScrollProgressButton() {
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let hideTimer = 0;

    const updateProgress = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const nextProgress = maxScroll > 0 ? Math.min(100, Math.max(0, Math.round((scrollTop / maxScroll) * 100))) : 0;
      setProgress(nextProgress);
    };

    const handleScroll = () => {
      updateProgress();
      setVisible(window.scrollY > 40);
      window.clearTimeout(hideTimer);
      hideTimer = window.setTimeout(() => setVisible(false), 950);
    };

    updateProgress();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateProgress);
    return () => {
      window.clearTimeout(hideTimer);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateProgress);
    };
  }, []);

  return (
    <button
      className={visible ? 'scroll-progress visible' : 'scroll-progress'}
      type="button"
      style={{ '--progress': `${progress * 3.6}deg` }}
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="Scroll to top"
    >
      <span>{progress}%</span>
    </button>
  );
}

function WhatsappButton() {
  const [open, setOpen] = useState(false);
  const defaultMessage = 'Hi Team, I want to know more about your Truck Permit services';
  const [message, setMessage] = useState(defaultMessage);
  const whatsappUrl = `https://wa.me/19595000099?text=${encodeURIComponent(message)}`;

  return (
    <div className="whatsapp-widget">
      {open ? (
        <div className="whatsapp-box">
          <div>
            <strong>Best Truck Permits</strong>
            <button type="button" aria-label="Close WhatsApp chat" onClick={() => setOpen(false)}><span className="close-icon" aria-hidden="true"></span></button>
          </div>
          <label>
            Message
            <textarea value={message} onChange={(event) => setMessage(event.target.value)} rows="4" />
          </label>
          <a href={whatsappUrl} target="_blank" rel="noreferrer">Send on WhatsApp <ArrowIcon /></a>
        </div>
      ) : null}
      <button className="whatsapp-chat" type="button" onClick={() => setOpen((current) => !current)} aria-label="Open WhatsApp chat">
        <span aria-hidden="true">WA</span>
        <strong>Let's Chat</strong>
      </button>
    </div>
  );
}

function ButtonLabelAnimator({ path }) {
  useEffect(() => {
    const applyLabels = () => {
      const targets = document.querySelectorAll('.btn, .header-phone');
      targets.forEach((target) => {
        if (target.classList.contains('btn') && !target.querySelector(':scope > span[aria-hidden="true"]')) {
          const arrow = document.createElement('span');
          arrow.className = 'btn-arrow-icon';
          arrow.setAttribute('aria-hidden', 'true');
          target.appendChild(arrow);
        }

        if (target.querySelector(':scope > .btn-label')) {
          target.removeAttribute('data-label');
          return;
        }

        const label = Array.from(target.childNodes)
          .filter((node) => node.nodeType === Node.TEXT_NODE)
          .map((node) => node.textContent)
          .join(' ')
          .replace(/\s+/g, ' ')
          .trim();

        if (label) {
          Array.from(target.childNodes)
            .filter((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim())
            .forEach((node) => node.remove());

          const labelWrap = document.createElement('span');
          labelWrap.className = 'btn-label';
          labelWrap.setAttribute('data-label', label);
          labelWrap.setAttribute('aria-hidden', 'true');

          const current = document.createElement('span');
          current.className = 'btn-label-current';
          current.textContent = label;

          const next = document.createElement('span');
          next.className = 'btn-label-next';
          next.textContent = label;

          labelWrap.append(current, next);
          const arrow = target.querySelector(':scope > span[aria-hidden="true"]:not(.btn-label)');
          target.insertBefore(labelWrap, arrow || null);
          target.setAttribute('aria-label', target.getAttribute('aria-label') || label);
          target.classList.add('has-button-label');
          target.removeAttribute('data-label');
        }
      });
    };
    applyLabels();
    const observer = new MutationObserver(applyLabels);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [path]);

  return null;
}

function App() {
  const [path, navigate] = useRoute();
  const [submissions, setSubmissions] = useLocalState('btp-submissions', []);
  const [posts, setPosts] = useLocalState('btp-posts', seedPosts);
  const [adminAuthed, setAdminAuthed] = useState(() => localStorage.getItem('btp-admin-auth') === 'true');

  useEffect(() => setMeta(path), [path]);

  useEffect(() => {
    if (path.startsWith('/admin')) import('./admin-blog.css');
  }, [path]);

  useEffect(() => {
    setPosts((current) => mergePosts(Array.isArray(current) ? current : []));
  }, [setPosts]);

  useEffect(() => {
    if (!path.startsWith('/blog') && !path.startsWith('/admin')) return undefined;
    const loadPosts = () => {
      apiRequest('posts.php')
        .then((data) => {
          if (Array.isArray(data.posts) && data.posts.length) {
            setPosts(mergePosts(data.posts));
          }
        })
        .catch(() => {});
    };
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(loadPosts, { timeout: 2500 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(loadPosts, 1500);
    return () => window.clearTimeout(id);
  }, [path, setPosts]);

  const page = useMemo(() => {
    const serviceSlug = path.startsWith('/services/') ? path.replace('/services/', '') : '';
    const postId = path.startsWith('/blog/') ? path.replace('/blog/', '') : '';
    const companyState = stateMarkets.find((item) => path === `/trucking-company-in-${item.slug}`);
    const permitState = stateMarkets.find((item) => path === `/truck-permits-in-${item.slug}`);
    if (serviceSlug) return <ServiceDetailPage service={services.find((service) => service.slug === serviceSlug)} navigate={navigate} />;
    if (postId) return <BlogPostPage post={posts.find((post) => post.id === postId)} navigate={navigate} />;
    if (companyState) return <StateMarketPage state={companyState} type="company" navigate={navigate} />;
    if (permitState) return <StateMarketPage state={permitState} type="permits" navigate={navigate} />;
    if (path === '/about') return <FullAboutPage navigate={navigate} />;
    if (path === '/services') return <ServicesPage navigate={navigate} />;
    if (path === '/packages') return <PackagesPage navigate={navigate} />;
    if (path === '/factoring') return <FactoringPage navigate={navigate} />;
    if (path === '/faq') return <FaqPage navigate={navigate} />;
    if (path === '/terms-of-service') return <PolicyPage page={policyPages.terms} navigate={navigate} />;
    if (path === '/privacy-policy') return <PolicyPage page={policyPages.privacy} navigate={navigate} />;
    if (path === '/refund-policy') return <PolicyPage page={policyPages.refund} navigate={navigate} />;
    if (path === '/disclaimer') return <PolicyPage page={policyPages.disclaimer} navigate={navigate} />;
    if (path === '/editorial-policy') return <PolicyPage page={policyPages.editorial} navigate={navigate} />;
    if (path === '/corrections-policy') return <PolicyPage page={policyPages.corrections} navigate={navigate} />;
    if (path === '/cookie-policy') return <PolicyPage page={policyPages.cookies} navigate={navigate} />;
    if (path === '/blog') return <BlogPage posts={posts} navigate={navigate} />;
    if (path === '/contact') return <ContactPage submissions={submissions} setSubmissions={setSubmissions} />;
    if (path === '/admin/login' || path === '/admin-login') return <AdminLoginPage navigate={navigate} setAdminAuthed={setAdminAuthed} />;
    if (path === '/admin') {
      return adminAuthed
        ? <AdminPage submissions={submissions} setSubmissions={setSubmissions} posts={posts} setPosts={setPosts} />
        : <AdminLoginPage navigate={navigate} setAdminAuthed={setAdminAuthed} />;
    }
    return <Home navigate={navigate} posts={posts} />;
  }, [adminAuthed, path, posts, submissions]);

  return (
    <>
      <Header path={path} navigate={navigate} setSubmissions={setSubmissions} />
      {page}
      <FooterBexon navigate={navigate} />
      <ScrollProgressButton />
      <WhatsappButton />
    </>
  );
}

createRoot(document.getElementById('root')).render(<App />);


